/**
 * Bulk Order State Management and Functionality
 * Handles product selection, order list management, and checkout
 */


(function() {
  'use strict';

  // ==========================================================================
  // State Management
  // ==========================================================================

  window.BULK_ORDER = {
    // Order items: [{ productId, variantId, quantity, title, variantTitle, price, image, handle }]
    orderList: [],

    // Cache parsed price-based discount tiers by raw metafield string
    discountTierCache: {},

    // Rules mirrored by the 424 Tiered Discounts app for storefront display.
    appDiscountRules: [],
    
    // Global customizations collected from blocks
    customizations: {},
    
    // Per-product notes: { "productId-variantId": "note text" }
    productNotes: {},
    
    // File references for file uploads (not persisted to localStorage)
    // { "Property Name": FileList }
    fileReferences: {},
    pendingUploadKeys: [],

    // Initialize from localStorage
    init: function() {
      this.rememberActiveFlow();
      this.load();
      this.updateUploadNotice();
      this.loadAppDiscountRules();
      this.updateAppDiscountTables();
      this.fileReferences = {}; // Always reset - files can't be restored from localStorage
      this.bindEvents();
      this.updateUI();
    },

    // Add item to order
    addItem: function(item) {
      var existingIndex = this.findItemIndex(item.productId, item.variantId);
      item.basePrice = this.normalizePrice(item.basePrice != null ? item.basePrice : item.price);
      item.price = item.basePrice;
      item.discountTiers = typeof item.discountTiers === 'string' ? item.discountTiers : '';
      
      if (existingIndex > -1) {
        // Update quantity if item exists
        this.orderList[existingIndex].quantity += item.quantity;
        this.orderList[existingIndex].basePrice = item.basePrice;
        this.orderList[existingIndex].price = item.basePrice;
        this.orderList[existingIndex].discountTiers = item.discountTiers;
        this.orderList[existingIndex].discountSource = item.discountSource || '';
      } else {
        this.orderList.push(item);
      }
      
      this.save();
      this.updateUI();
      this.dispatchEvent('itemAdded', item);
    },

    // Remove item from order
    removeItem: function(productId, variantId) {
      var index = this.findItemIndex(productId, variantId);
      if (index > -1) {
        var removed = this.orderList.splice(index, 1)[0];
        delete this.productNotes[productId + '-' + variantId];
        this.save();
        this.updateUI();
        this.dispatchEvent('itemRemoved', removed);
      }
    },

    // Update item quantity
    updateQuantity: function(productId, variantId, quantity) {
      var index = this.findItemIndex(productId, variantId);
      if (index > -1) {
        if (quantity <= 0) {
          this.removeItem(productId, variantId);
        } else {
          this.orderList[index].quantity = quantity;
          this.save();
          this.updateUI();
        }
      }
    },

    // Find item index
    findItemIndex: function(productId, variantId) {
      for (var i = 0; i < this.orderList.length; i++) {
        if (this.orderList[i].productId == productId && 
            this.orderList[i].variantId == variantId) {
          return i;
        }
      }
      return -1;
    },

    // Check if item is in order
    isInOrder: function(productId, variantId) {
      return this.findItemIndex(productId, variantId) > -1;
    },

    // Get total items count
    getTotalItems: function() {
      return this.orderList.reduce(function(sum, item) {
        return sum + item.quantity;
      }, 0);
    },

    // Normalize a price value in cents
    normalizePrice: function(value) {
      var normalized = parseInt(value, 10);
      return isNaN(normalized) ? 0 : normalized;
    },

    // Get the base variant price for an item before any tier preview is applied
    getItemBasePrice: function(item) {
      if (!item) return 0;
      if (item.basePrice != null) return this.normalizePrice(item.basePrice);
      return this.normalizePrice(item.price);
    },

    // Get the total quantity selected for a product across all variants
    getProductQuantity: function(productId) {
      return this.orderList.reduce(function(sum, item) {
        if (item.productId == productId) {
          return sum + item.quantity;
        }
        return sum;
      }, 0);
    },

    // Get the raw discount tier metafield string for a product from state or DOM
    getProductDiscountTiersRaw: function(productId, fallbackItem) {
      if (fallbackItem && typeof fallbackItem.discountTiers === 'string' && fallbackItem.discountTiers !== '') {
        return fallbackItem.discountTiers;
      }

      for (var i = 0; i < this.orderList.length; i++) {
        if (this.orderList[i].productId == productId && typeof this.orderList[i].discountTiers === 'string' && this.orderList[i].discountTiers !== '') {
          return this.orderList[i].discountTiers;
        }
      }

      var productCard = document.querySelector('.bulk-order-product-card[data-product-id="' + productId + '"]');
      return productCard ? (productCard.getAttribute('data-discount-tiers') || '') : '';
    },

    // Load the rules index exposed by the 424 Tiered Discounts app embed.
    loadAppDiscountRules: function() {
      var rules = [];

      if (Array.isArray(window.TIER_PRICING_RULES_INDEX)) {
        rules = window.TIER_PRICING_RULES_INDEX;
      } else if (Array.isArray(window.tierPricingRulesIndex)) {
        rules = window.tierPricingRulesIndex;
      } else {
        var script = document.querySelector(
          'script[data-tier-pricing-rules-index], script#tier-pricing-rules-index'
        );
        if (script && script.textContent) {
          try {
            var parsed = JSON.parse(script.textContent);
            if (Array.isArray(parsed)) {
              rules = parsed;
            } else if (parsed && Array.isArray(parsed.rules)) {
              rules = parsed.rules;
            }
          } catch (error) {
            console.warn('Could not parse 424 tier pricing rules index:', error);
          }
        }
      }

      this.appDiscountRules = rules.filter(function(rule) {
        return rule && Array.isArray(rule.tiers) && rule.tiers.length > 0;
      });
    },

    getProductCardForPricing: function(productId, fallbackCard) {
      if (fallbackCard) return fallbackCard;
      return document.querySelector('.bulk-order-product-card[data-product-id="' + productId + '"]');
    },

    shouldUseAppDiscountRules: function(card) {
      var source = card
        ? card.getAttribute('data-discount-source')
        : (window.BULK_ORDER_SETTINGS && window.BULK_ORDER_SETTINGS.discountSource);
      return source === 'app_rules';
    },

    getAppDiscountRuleForProduct: function(productId, fallbackCard) {
      var card = this.getProductCardForPricing(productId, fallbackCard);
      if (!card || !this.shouldUseAppDiscountRules(card) || !this.appDiscountRules.length) {
        return null;
      }

      for (var i = 0; i < this.appDiscountRules.length; i++) {
        var rule = this.appDiscountRules[i];
        if (this.appRuleMatchesCard(rule, card, productId)) {
          return rule;
        }
      }

      return null;
    },

    appRuleMatchesCard: function(rule, card, productId) {
      var targetIds = Array.isArray(rule.target_ids) ? rule.target_ids : [];
      if (!targetIds.length) return false;

      switch (rule.target_type) {
        case 'product':
          return targetIds.some(function(targetId) {
            return String(targetId).split('/').pop() === String(productId);
          });
        case 'collection': {
          var collectionIds = (card.getAttribute('data-collection-ids') || '')
            .split(',')
            .map(function(id) { return id.trim(); })
            .filter(Boolean);
          return targetIds.some(function(targetId) {
            return collectionIds.indexOf(String(targetId).split('/').pop()) !== -1;
          });
        }
        case 'tag': {
          var productTags = this.parseJsonAttribute(card.getAttribute('data-product-tags'), []);
          return targetIds.some(function(targetTag) {
            return productTags.indexOf(targetTag) !== -1;
          });
        }
        default:
          return false;
      }
    },

    parseJsonAttribute: function(value, fallback) {
      if (!value) return fallback;
      try {
        return JSON.parse(value.replace(/&quot;/g, '"'));
      } catch {
        return fallback;
      }
    },

    getSortedAppTiers: function(rule) {
      if (!rule || !Array.isArray(rule.tiers)) return [];
      return rule.tiers
        .filter(function(tier) {
          return tier &&
            Number.isFinite(Number(tier.min_qty)) &&
            Number(tier.min_qty) >= 1 &&
            Number.isFinite(Number(tier.value)) &&
            Number(tier.value) >= 0;
        })
        .sort(function(a, b) {
          return Number(a.min_qty) - Number(b.min_qty);
        });
    },

    getAppTierQuantity: function(rule, productId, options) {
      if (rule && rule.quantity_scope === 'product') {
        return options && options.productQuantity != null
          ? parseInt(options.productQuantity, 10) || 0
          : this.getProductQuantity(productId);
      }

      if (options && options.lineQuantity != null) {
        return parseInt(options.lineQuantity, 10) || 0;
      }

      if (options && options.item) {
        return parseInt(options.item.quantity, 10) || 0;
      }

      return options && options.productQuantity != null
        ? parseInt(options.productQuantity, 10) || 0
        : this.getProductQuantity(productId);
    },

    calculateAppTierUnitPrice: function(basePrice, tier) {
      var normalizedBasePrice = this.normalizePrice(basePrice);
      var value = Number(tier && tier.value);
      if (!Number.isFinite(value) || value < 0) return normalizedBasePrice;

      switch (tier && tier.discount_type) {
        case 'percentage':
          return Math.round(normalizedBasePrice * ((100 - value) / 100));
        case 'fixed_amount':
          return Math.max(0, normalizedBasePrice - Math.round(value * 100));
        case 'fixed_price':
          return Math.min(normalizedBasePrice, Math.round(value * 100));
        default:
          return normalizedBasePrice;
      }
    },

    getBestAppTierForQuantity: function(tiers, quantity) {
      var best = null;
      for (var i = 0; i < tiers.length; i++) {
        var tier = tiers[i];
        if (quantity >= Number(tier.min_qty) && (!best || Number(tier.min_qty) > Number(best.min_qty))) {
          best = tier;
        }
      }
      return best;
    },

    // Parse supported price-based tiers such as "1-5:$30 each|6+:$25 each"
    parsePriceBasedDiscountTiers: function(discountTiersRaw) {
      if (!discountTiersRaw) return [];
      if (this.discountTierCache[discountTiersRaw]) return this.discountTierCache[discountTiersRaw];

      var parsedTiers = [];
      var tiers = discountTiersRaw.split('|');

      for (var i = 0; i < tiers.length; i++) {
        var tier = tiers[i];
        var separatorIndex = tier.indexOf(':');
        if (separatorIndex === -1) continue;

        var range = this.parseDiscountRange(tier.slice(0, separatorIndex));
        var unitPrice = this.parsePricePerItem(tier.slice(separatorIndex + 1));

        if (!range || unitPrice == null) continue;

        parsedTiers.push({
          min: range.min,
          max: range.max,
          unitPrice: unitPrice
        });
      }

      parsedTiers.sort(function(a, b) {
        return a.min - b.min;
      });

      this.discountTierCache[discountTiersRaw] = parsedTiers;
      return parsedTiers;
    },

    // Parse supported quantity ranges such as "1-5" or "250+"
    parseDiscountRange: function(rangeText) {
      if (!rangeText) return null;

      var trimmed = rangeText.trim();
      var betweenMatch = trimmed.match(/^(\d+)\s*-\s*(\d+)$/);
      if (betweenMatch) {
        return {
          min: parseInt(betweenMatch[1], 10),
          max: parseInt(betweenMatch[2], 10)
        };
      }

      var plusMatch = trimmed.match(/^(\d+)\s*\+$/);
      if (plusMatch) {
        return {
          min: parseInt(plusMatch[1], 10),
          max: null
        };
      }

      return null;
    },

    // Parse supported price-per-item text such as "$13.50 each"
    parsePricePerItem: function(valueText) {
      if (!valueText) return null;
      if (!/\b(each|ea)\b/i.test(valueText)) return null;

      var amountMatch = valueText.match(/\$?\s*([\d,]+(?:\.\d{1,2})?)/i);
      if (!amountMatch) return null;

      var amount = parseFloat(amountMatch[1].replace(/,/g, ''));
      if (isNaN(amount)) return null;

      return Math.round(amount * 100);
    },

    // Get the effective unit price using product-wide selected quantity
    getEffectiveUnitPrice: function(productId, basePrice, options) {
      var normalizedBasePrice = this.normalizePrice(basePrice);
      var appRule = this.getAppDiscountRuleForProduct(productId, options && options.card);
      if (appRule) {
        var appTiers = this.getSortedAppTiers(appRule);
        var appQuantity = this.getAppTierQuantity(appRule, productId, options);
        var appTier = this.getBestAppTierForQuantity(appTiers, appQuantity);
        return appTier ? this.calculateAppTierUnitPrice(normalizedBasePrice, appTier) : normalizedBasePrice;
      }

      var productQuantity = options && options.productQuantity != null
        ? parseInt(options.productQuantity, 10) || 0
        : this.getProductQuantity(productId);

      if (productQuantity <= 0) return normalizedBasePrice;

      var discountTiersRaw = options && typeof options.discountTiersRaw === 'string'
        ? options.discountTiersRaw
        : this.getProductDiscountTiersRaw(productId, options && options.item);
      var tiers = this.parsePriceBasedDiscountTiers(discountTiersRaw);

      if (!tiers.length) return normalizedBasePrice;

      var matchedUnitPrice = null;
      for (var i = 0; i < tiers.length; i++) {
        var tier = tiers[i];
        if (productQuantity >= tier.min && (tier.max == null || productQuantity <= tier.max)) {
          matchedUnitPrice = tier.unitPrice;
        }
      }

      return matchedUnitPrice != null ? matchedUnitPrice : normalizedBasePrice;
    },

    formatAppTierLabel: function(tier, nextTier) {
      var minQty = Number(tier.min_qty);
      if (nextTier) {
        return minQty + '-' + (Number(nextTier.min_qty) - 1);
      }
      return minQty + '+';
    },

    renderAppDiscountTableRows: function(rule, basePrice) {
      var tiers = this.getSortedAppTiers(rule);
      if (!tiers.length) return '';

      var rows = [];
      if (Number(tiers[0].min_qty) > 1) {
        rows.push(
          '<tr><td>1-' + (Number(tiers[0].min_qty) - 1) + '</td><td>' +
          this.formatMoney(basePrice) + ' each</td></tr>'
        );
      }

      for (var i = 0; i < tiers.length; i++) {
        var tier = tiers[i];
        var tierPrice = this.calculateAppTierUnitPrice(basePrice, tier);
        rows.push(
          '<tr><td>' + this.formatAppTierLabel(tier, tiers[i + 1]) + '</td><td>' +
          this.formatMoney(tierPrice) + ' each</td></tr>'
        );
      }

      return rows.join('');
    },

    updateAppDiscountTables: function() {
      var self = this;
      document.querySelectorAll('.bulk-order-product-card[data-discount-source="app_rules"]').forEach(function(card) {
        var productId = card.getAttribute('data-product-id');
        var rule = self.getAppDiscountRuleForProduct(productId, card);
        var details = card.querySelector('.bulk-order-product-card__discounts');
        var body = card.querySelector('[data-discount-tiers-body]');
        var firstVariant = card.querySelector('.bulk-order-single-variant');
        var basePrice = firstVariant
          ? parseInt(firstVariant.getAttribute('data-price'), 10) || 0
          : self.normalizePrice(card.getAttribute('data-variants') ? self.getLowestVariantPrice(card) : 0);

        if (!details || !body || !rule || basePrice <= 0) {
          if (details && !(card.getAttribute('data-discount-tiers') || '')) {
            details.hidden = true;
          }
          return;
        }

        var header = details.querySelector('th:nth-child(2)');
        if (header) header.textContent = 'Price each';
        body.innerHTML = self.renderAppDiscountTableRows(rule, basePrice);
        details.hidden = false;
      });
    },

    getLowestVariantPrice: function(card) {
      var variants = [];
      try {
        variants = JSON.parse((card.getAttribute('data-variants') || '[]').replace(/&quot;/g, '"'));
      } catch {
        variants = [];
      }

      return variants.reduce(function(lowest, variant) {
        var price = parseInt(variant.price, 10) || 0;
        if (price <= 0) return lowest;
        return lowest === 0 ? price : Math.min(lowest, price);
      }, 0);
    },

    // Get the effective unit price for an existing order item
    getItemUnitPrice: function(item) {
      var basePrice = this.getItemBasePrice(item);
      return this.getEffectiveUnitPrice(item.productId, basePrice, {
        productQuantity: this.getProductQuantity(item.productId),
        lineQuantity: item.quantity,
        discountTiersRaw: this.getProductDiscountTiersRaw(item.productId, item),
        item: item
      });
    },

    // Get the effective line price for an existing order item
    getItemLinePrice: function(item) {
      return this.getItemUnitPrice(item) * item.quantity;
    },

    // Get total price
    getTotalPrice: function() {
      var self = this;
      return this.orderList.reduce(function(sum, item) {
        return sum + self.getItemLinePrice(item);
      }, 0);
    },

    // Set customization value
    setCustomization: function(key, value) {
      this.customizations[key] = value;
      this.save();
    },

    // Set product note
    setProductNote: function(productId, variantId, note) {
      this.productNotes[productId + '-' + variantId] = note;
      this.save();
    },

    // Get product note
    getProductNote: function(productId, variantId) {
      return this.productNotes[productId + '-' + variantId] || '';
    },

    // Clear order
    clearOrder: function() {
      this.orderList = [];
      this.customizations = {};
      this.productNotes = {};
      this.fileReferences = {};
      this.pendingUploadKeys = [];
      this.save();
      this.clearActiveFlow();
      this.updateUI();
      this.updateUploadNotice();
      this.dispatchEvent('orderCleared');
    },

    getUploadInputs: function() {
      return Array.from(document.querySelectorAll(
        '#BulkOrderBlocksContainer input[type="file"][name^="properties["], #BulkOrderBlocksTarget input[type="file"][name^="properties["]'
      ));
    },

    getUploadKey: function(input) {
      var match = input.name.match(/^properties\[(.*)\]$/);
      return match ? match[1] : '';
    },

    getUploadKeys: function() {
      var self = this;
      return Array.from(new Set(Object.keys(this.fileReferences).concat(
        this.pendingUploadKeys,
        this.getUploadInputs().map(function(input) { return self.getUploadKey(input); })
      ))).filter(Boolean);
    },

    isUploadActive: function(input) {
      if (input.matches(':disabled')) return false;
      // The native input is intentionally hidden. Check the upload UI instead,
      // stopping before the wizard containers, which hide inactive steps.
      var element = input.parentElement;
      while (element && element.id !== 'BulkOrderBlocksContainer' && element.id !== 'BulkOrderBlocksTarget') {
        if (element.hidden || element.getAttribute('aria-hidden') === 'true' || getComputedStyle(element).display === 'none') return false;
        element = element.parentElement;
      }
      return true;
    },

    validateUploads: function() {
      var self = this;
      var errors = [];
      var inputs = this.getUploadInputs();
      var keys = this.getUploadKeys();
      var nextReferences = {};
      keys.forEach(function(key) {
        delete self.customizations[key];
        var matching = inputs.filter(function(input) { return self.getUploadKey(input) === key; });
        var active = matching.filter(function(input) { return self.isUploadActive(input); });
        var selected = active.find(function(input) { return input.files && input.files.length; });
        if (selected) {
          nextReferences[key] = selected.files;
          self.customizations[key] = Array.from(selected.files).map(function(file) { return file.name; }).join(', ');
          self.pendingUploadKeys = self.pendingUploadKeys.filter(function(value) { return value !== key; });
        } else if (matching.length && !active.length) {
          self.pendingUploadKeys = self.pendingUploadKeys.filter(function(value) { return value !== key; });
        }
        active.forEach(function(input) {
          var invalid = (input.required || input.getAttribute('aria-required') === 'true') && !input.files?.length;
          var wrapper = input.closest('.product-file-upload, .product-personalization');
          var error = wrapper?.querySelector('[ref="errorMessage"], .error-message');
          if (invalid) {
            input.setAttribute('aria-invalid', 'true');
            if (error) error.hidden = false;
            errors.push(key);
          } else {
            input.removeAttribute('aria-invalid');
            if (error && input.files?.length) error.hidden = true;
          }
        });
      });
      this.fileReferences = nextReferences;
      this.save();
      this.updateUploadNotice();
      if (this.pendingUploadKeys.length) {
        errors.unshift(this.uploadMessage('uploadRecovery', 'Please reselect your previous uploads, or discard them to continue.'));
      }
      return { valid: errors.length === 0, errors: errors };
    },

    uploadMessage: function(key, fallback) {
      return window.BULK_ORDER_SETTINGS?.[key] || fallback;
    },

    updateUploadNotice: function() {
      var content = document.querySelector('.bulk-order-content');
      if (!content) return;
      var notice = content.querySelector('[data-upload-recovery]');
      if (!notice) return;
      notice.hidden = !this.pendingUploadKeys.length;
      if (notice.hidden) return;
      var self = this;
      var review = notice.querySelector('[data-upload-review]');
      var discard = notice.querySelector('[data-upload-discard]');
      var step = document.getElementById('bulk-order')?.getAttribute('data-step');
      var pendingInput = this.getUploadInputs().find(function(input) {
        return self.pendingUploadKeys.includes(self.getUploadKey(input)) && self.isUploadActive(input);
      });
      var upload = pendingInput?.closest('.product-file-upload, .product-personalization');
      var inline = step === '2' && !!upload;
      if (inline) {
        upload.appendChild(notice);
      } else {
        content.prepend(notice);
      }
      review.hidden = inline;
      if (notice.dataset.bound !== 'true') {
        review.addEventListener('click', function() {
          showStep('2');
          notice.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
        discard.addEventListener('click', function() {
          self.pendingUploadKeys.forEach(function(key) { delete self.customizations[key]; });
          self.pendingUploadKeys = [];
          self.save();
          self.updateUploadNotice();
        });
        notice.dataset.bound = 'true';
      }
      notice.querySelector('[data-upload-recovery-message]').textContent = this.uploadMessage('uploadRecovery', 'Please reselect your previous uploads, or discard them to continue.') + ' (' + this.pendingUploadKeys.join(', ') + ')';
    },

    // Save to localStorage
    save: function() {
      try {
        var savedCustomizations = Object.assign({}, this.customizations);
        var transferred = this.designTransferProperties || {};
        Object.keys(transferred).forEach(function(key) {
          if (savedCustomizations[key] === transferred[key]) delete savedCustomizations[key];
        });
        var filePropertyKeys = this.getUploadKeys();
        filePropertyKeys.forEach(function(key) { delete savedCustomizations[key]; });
        var references = this.fileReferences;
        var pendingUploadKeys = Array.from(new Set(this.pendingUploadKeys.concat(
          Object.keys(references).filter(function(key) { return references[key]?.length; })
        )));
        localStorage.setItem(this.getStorageKey(), JSON.stringify({
          orderList: this.orderList,
          customizations: savedCustomizations,
          productNotes: this.productNotes,
          filePropertyKeys: filePropertyKeys,
          pendingUploadKeys: pendingUploadKeys
        }));
      } catch (e) {
        console.warn('Could not save bulk order state:', e);
      }
    },

    // Load from localStorage
    load: function() {
      try {
        var saved = localStorage.getItem(this.getStorageKey());
        if (saved) {
          var state = JSON.parse(saved);
          this.orderList = state.orderList || [];
          this.orderList = this.orderList.map(function(item) {
            if (item.basePrice == null) item.basePrice = item.price;
            if (typeof item.discountTiers !== 'string') item.discountTiers = '';
            if (typeof item.discountSource !== 'string') item.discountSource = '';
            return item;
          });
          this.customizations = state.customizations || {};
          this.pendingUploadKeys = Array.isArray(state.pendingUploadKeys) ? state.pendingUploadKeys : [];
          var keys = this.getUploadKeys().concat(Array.isArray(state.filePropertyKeys) ? state.filePropertyKeys : []);
          var self = this;
          keys.forEach(function(key) {
            // Migrate older drafts that saved filenames as ordinary properties.
            if (self.customizations[key]) self.pendingUploadKeys.push(key);
            delete self.customizations[key];
          });
          this.pendingUploadKeys = Array.from(new Set(this.pendingUploadKeys));
          this.productNotes = state.productNotes || {};
        }
      } catch (e) {
        console.warn('Could not load bulk order state:', e);
      }
    },

    getStorageKey: function() {
      var settings = window.BULK_ORDER_SETTINGS || {};
      return settings.storageKey || 'bulk_order_state';
    },

    getPageSlug: function() {
      var settings = window.BULK_ORDER_SETTINGS || {};
      return settings.pageSlug || '';
    },

    rememberActiveFlow: function() {
      var pageSlug = this.getPageSlug();
      if (!pageSlug) return;

      try {
        localStorage.setItem('bulk_order_active_page_slug', pageSlug);
      } catch (e) {
        console.warn('Could not remember active bulk order flow:', e);
      }
    },

    clearActiveFlow: function() {
      var pageSlug = this.getPageSlug();
      if (!pageSlug) return;

      try {
        if (localStorage.getItem('bulk_order_active_page_slug') === pageSlug) {
          localStorage.removeItem('bulk_order_active_page_slug');
        }
      } catch (e) {
        console.warn('Could not clear active bulk order flow:', e);
      }
    },

    // Dispatch custom event
    dispatchEvent: function(name, detail) {
      document.dispatchEvent(new CustomEvent('bulkOrder:' + name, { detail: detail }));
    },

    // Bind global events
    bindEvents: function() {
      var self = this;
      
      // Listen for block input changes to capture customizations
      document.addEventListener('change', function(e) {
        var input = e.target;
        if (input.name && input.name.startsWith('properties[')) {
          var key = input.name.match(/properties\[([^\]]+)\]/);
          if (key && key[1]) {
            var value;
            
            if (input.type === 'file') {
              if (!self.getUploadInputs().includes(input)) return;
              self.pendingUploadKeys = self.pendingUploadKeys.filter(function(value) { return value !== key[1]; });
              // For file inputs, capture the actual filename(s), not the fakepath
              if (input.files && input.files.length > 0) {
                if (input.files.length === 1) {
                  value = input.files[0].name;
                } else {
                  // Multiple files - join names
                  value = Array.from(input.files).map(function(f) { return f.name; }).join(', ');
                }
                // Also store file references for potential later use
                self.fileReferences = self.fileReferences || {};
                self.fileReferences[key[1]] = input.files;
              } else {
                value = '';
                if (self.fileReferences) {
                  delete self.fileReferences[key[1]];
                }
              }
            } else if (input.type === 'checkbox') {
              value = input.checked;
            } else if (input.type === 'radio') {
              if (!input.checked) return;
              value = input.value;
            } else {
              value = input.value;
            }
            
            self.setCustomization(key[1], value);
            if (input.type === 'file') self.updateUploadNotice();
          }
        }
      });
    },

    // Update all UI elements
    updateUI: function() {
      this.updateSidebar();
      this.updateProductCards();
      this.updateNavigation();
      this.updateOrderSummary();
    },

    // Update sidebar
    updateSidebar: function() {
      var sidebar = document.querySelector('.bulk-order-sidebar');
      if (!sidebar) return;

      var itemsContainer = sidebar.querySelector('.bulk-order-sidebar__items');
      var emptyState = sidebar.querySelector('.bulk-order-sidebar__empty');
      var countEl = sidebar.querySelector('.bulk-order-sidebar__count');
      var totalEl = sidebar.querySelector('.bulk-order-sidebar__total-value');
      var body = sidebar.querySelector('.bulk-order-sidebar__body');

      var totalItems = this.getTotalItems();
      var totalPrice = this.getTotalPrice();

      if (this.orderList.length === 0) {
        if (itemsContainer) itemsContainer.innerHTML = '';
        if (emptyState) emptyState.style.display = 'block';
        sidebar.classList.remove('has-items');
      } else {
        if (emptyState) emptyState.style.display = 'none';
        sidebar.classList.add('has-items');
        if (itemsContainer) {
          itemsContainer.innerHTML = this.orderList.map(function(item) {
            return BULK_ORDER.renderSidebarItem(item);
          }).join('');
        }
      }

      if (countEl) countEl.textContent = totalItems + ' item' + (totalItems !== 1 ? 's' : '');
      if (totalEl) totalEl.textContent = this.formatMoney(totalPrice);
    },

    // Render sidebar item
    renderSidebarItem: function(item) {
      return '<div class="bulk-order-sidebar__item" data-product-id="' + item.productId + '" data-variant-id="' + item.variantId + '">' +
        '<div class="bulk-order-sidebar__item-image">' +
          (item.image ? '<img src="' + item.image + '&width=96" alt="">' : '') +
        '</div>' +
        '<div class="bulk-order-sidebar__item-info">' +
          '<p class="bulk-order-sidebar__item-title">' + this.escapeHtml(item.title) + '</p>' +
          (item.variantTitle ? '<p class="bulk-order-sidebar__item-variant">' + this.escapeHtml(item.variantTitle) + '</p>' : '') +
          '<p class="bulk-order-sidebar__item-qty">Qty: ' + item.quantity + '</p>' +
        '</div>' +
        '<button type="button" class="bulk-order-sidebar__item-remove" aria-label="Remove item">' +
          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
            '<line x1="18" y1="6" x2="6" y2="18"></line>' +
            '<line x1="6" y1="6" x2="18" y2="18"></line>' +
          '</svg>' +
        '</button>' +
      '</div>';
    },

    // Update product cards (mark as in order and sync quantities)
    updateProductCards: function() {
      var self = this;
      document.querySelectorAll('.bulk-order-product-card').forEach(function(card) {
        var productId = card.getAttribute('data-product-id');
        
        // Find if any variant of this product is in order
        var hasItemInOrder = false;
        self.orderList.forEach(function(item) {
          if (item.productId == productId) {
            hasItemInOrder = true;
          }
        });
        
        if (hasItemInOrder) {
          card.classList.add('is-in-order');
        } else {
          card.classList.remove('is-in-order');
        }

        renderAddedVariantsInCard(card);
        updateSelectionRow(card);
        updateCardStockVisibility(card);
      });
    },

    // Update navigation button state
    updateNavigation: function() {
      var nextBtn = document.getElementById('BulkOrderNextStep1');
      if (nextBtn) {
        nextBtn.disabled = this.orderList.length === 0;
      }
    },

    // Update order summary in Step 2/3
    updateOrderSummary: function() {
      // Handled by step-specific renderers
    },

    // Format money
    formatMoney: function(cents) {
      return '$' + (cents / 100).toFixed(2);
    },

    // Escape HTML
    escapeHtml: function(str) {
      if (!str) return '';
      return str.replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');
    },

    // Create a short-lived identifier that keeps all merchandise and fee lines
    // from this checkout linked together in the cart.
    createCartGroupId: function() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return 'bulk-' + window.crypto.randomUUID();
      }
      return 'bulk-' + Date.now() + '-' + Math.random().toString(36).slice(2);
    },

    // Build the configured hidden fee products using the aggregate Bulk Order
    // quantity and the same customization blocks as the regular product form.
    collectConfiguredFees: function(cartGroupId) {
      var manager = window.customOptionFeesManager;
      var feeRoot = document.getElementById('BulkOrderBlocksTarget') || document.getElementById('BulkOrderBlocksContainer');
      var firstOrderItem = this.orderList[0];

      if (!manager || typeof manager.collectBulkOrderFees !== 'function' || !feeRoot || !firstOrderItem) {
        return Promise.resolve([]);
      }

      return manager.collectBulkOrderFees(
        feeRoot,
        String(firstOrderItem.productId),
        this.getTotalItems(),
        undefined,
        cartGroupId,
        this.customizations
      );
    },

    // File uploads must add merchandise as multipart form requests first, so
    // their fee lines are added in a follow-up request linked by group id.
    addConfiguredFees: function(cartGroupId) {
      var manager = window.customOptionFeesManager;
      return this.collectConfiguredFees(cartGroupId).then(function(fees) {
        if (!fees.length) return;
        return manager.addFeesToCart(fees, { refreshCart: false });
      });
    },

    // Add all items to Shopify cart
    addToCart: function() {
      var self = this;
      var uploadValidation = this.validateUploads();
      if (!uploadValidation.valid) {
        showStep('2');
        return Promise.reject(new Error(this.uploadMessage('uploadRequired', 'Please review your uploads before continuing.') + ' ' + uploadValidation.errors.join(' ')));
      }
      var cartGroupId = this.createCartGroupId();
      var hasFiles = self.fileReferences && Object.keys(self.fileReferences).length > 0;
      var stockValidation = validateOrderInventory();

      if (!stockValidation.valid) {
        return Promise.reject(new Error(stockValidation.errors.map(function(error) {
          return error.message;
        }).join(' ')));
      }
      
      // If there are file uploads, we need to use FormData and add items one at a time
      // because Shopify's file upload mechanism requires form-based submission
      if (hasFiles) {
        return self.addToCartWithFiles(cartGroupId);
      }
      
      // No files - use the faster bulk JSON approach
      var items = this.orderList.map(function(item) {
        var properties = Object.assign({}, self.customizations);
        properties._bulk_order_group = cartGroupId;
        var note = self.getProductNote(item.productId, item.variantId);
        if (note) {
          properties['Item Note'] = note;
        }
        return {
          id: item.variantId,
          quantity: item.quantity,
          properties: properties
        };
      });

      return self.collectConfiguredFees(cartGroupId)
      .then(function(fees) {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items.concat(fees) })
        });
      })
      .then(function(response) {
        if (!response.ok) {
          return response.json().then(function(err) {
            throw new Error(err.description || 'Cart add failed');
          }).catch(function(error) {
            if (error instanceof Error) throw error;
            throw new Error('Cart add failed');
          });
        }
        return response.json();
      })
      .then(function(data) {
        self.clearOrder();
        window.location.href = '/cart';
        return data;
      });
    },

    // Add items to cart with file uploads using FormData
    // Shopify only processes file uploads via FormData, not JSON
    addToCartWithFiles: function(cartGroupId) {
      var self = this;
      var itemsToAdd = this.orderList.slice(); // Copy the order list
      
      // Get the set of keys that have file uploads
      var fileKeys = self.fileReferences ? Object.keys(self.fileReferences) : [];
      
      // Helper function to add a single item with FormData
      function addSingleItem(item) {
        return new Promise(function(resolve, reject) {
          var formData = new FormData();
          
          // Add the variant ID and quantity
          formData.append('id', item.variantId);
          formData.append('quantity', item.quantity);
          
          // Add all customizations as properties
          var customizations = Object.assign({}, self.customizations);
          customizations._bulk_order_group = cartGroupId;
          var note = self.getProductNote(item.productId, item.variantId);
          if (note) {
            customizations['Item Note'] = note;
          }
          
          // First, add file properties (actual files)
          fileKeys.forEach(function(key) {
            var files = self.fileReferences[key];
            if (files && files.length > 0) {
              for (var i = 0; i < files.length; i++) {
                formData.append('properties[' + key + ' ' + (i + 1) + ']', files[i]);
              }
            }
          });
          
          // Then add non-file properties (skip keys that have files)
          Object.keys(customizations).forEach(function(key) {
            // Skip if this key has a file - the file takes precedence
            if (fileKeys.indexOf(key) > -1) {
              return;
            }
            
            var value = customizations[key];
            if (value !== '' && value !== null && value !== undefined) {
              formData.append('properties[' + key + ']', value);
            }
          });
          
          fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          })
          .then(function(response) {
            if (!response.ok) {
              return response.json().then(function(err) {
                throw new Error(err.description || 'Cart add failed');
              });
            }
            return response.json();
          })
          .then(resolve)
          .catch(reject);
        });
      }
      
      // Add items sequentially to preserve order and handle files properly
      // Files can only be uploaded one item at a time
      return itemsToAdd.reduce(function(promise, item) {
        return promise.then(function() {
          return addSingleItem(item);
        });
      }, Promise.resolve())
      .then(function() {
        return self.addConfiguredFees(cartGroupId);
      })
      .then(function() {
        self.clearOrder();
        // Clear file references after successful upload
        self.fileReferences = {};
        window.location.href = '/cart';
      });
    }
  };

  // ==========================================================================
  // Description Toggle
  // ==========================================================================

  function initDescriptionToggles() {
    var toggles = document.querySelectorAll('.bulk-order-description__toggle');
    
    toggles.forEach(function(toggle) {
      toggle.addEventListener('click', function() {
        var textEl = this.previousElementSibling;
        var isExpanded = this.getAttribute('aria-expanded') === 'true';
        
        if (isExpanded) {
          // Collapse
          textEl.setAttribute('data-truncated', 'true');
          this.setAttribute('aria-expanded', 'false');
          this.textContent = 'Show more';
        } else {
          // Expand
          textEl.setAttribute('data-truncated', 'false');
          this.setAttribute('aria-expanded', 'true');
          this.textContent = 'Show less';
        }
      });
    });
  }

  // ==========================================================================
  // Enhanced Dropdown Component with Cascading Filter
  // ==========================================================================

  /**
   * Get all variants for a product card
   */
  function getProductVariants(card) {
    try {
      var variantsData = card.getAttribute('data-variants');
      if (variantsData) {
        return JSON.parse(variantsData.replace(/&quot;/g, '"'));
      }
    } catch (e) {
      console.error('Error parsing variants:', e);
    }
    return [];
  }

  function getProductCardByProductId(productId) {
    return document.querySelector('.bulk-order-product-card[data-product-id="' + productId + '"]');
  }

  function getVariantById(card, variantId) {
    if (!card || !variantId) return null;

    var variants = getProductVariants(card);
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(variantId)) {
        return variants[i];
      }
    }

    return null;
  }

  function getSelectedVariantInventoryFallback(card, variantId) {
    if (!card || !variantId) return null;

    var selectedOption = card.querySelector('.bulk-order-dropdown__option.is-selected[data-variant-id="' + variantId + '"]');
    if (selectedOption) {
      return {
        available: selectedOption.getAttribute('data-variant-available'),
        inventoryManagement: selectedOption.getAttribute('data-variant-inventory-management'),
        inventoryPolicy: selectedOption.getAttribute('data-variant-inventory-policy'),
        inventoryQuantity: selectedOption.getAttribute('data-variant-inventory-quantity')
      };
    }

    var singleVariant = card.querySelector('.bulk-order-single-variant');
    if (singleVariant && String(singleVariant.value) === String(variantId)) {
      return {
        available: singleVariant.getAttribute('data-variant-available'),
        inventoryManagement: singleVariant.getAttribute('data-variant-inventory-management'),
        inventoryPolicy: singleVariant.getAttribute('data-variant-inventory-policy'),
        inventoryQuantity: singleVariant.getAttribute('data-variant-inventory-quantity')
      };
    }

    return null;
  }

  function parseInventoryBoolean(value, fallback) {
    if (value === true || value === false) return value;
    if (value === 'true') return true;
    if (value === 'false') return false;
    return fallback;
  }

  function getVariantInventoryState(variant, fallbackItem, fallbackData) {
    var inventoryManagement = variant && variant.inventory_management != null
      ? variant.inventory_management
      : (fallbackData && fallbackData.inventoryManagement) || (fallbackItem && fallbackItem.inventoryManagement) || '';
    var inventoryPolicy = variant && variant.inventory_policy != null
      ? variant.inventory_policy
      : (fallbackData && fallbackData.inventoryPolicy) || (fallbackItem && fallbackItem.inventoryPolicy) || '';
    var inventoryQuantityRaw = variant && variant.inventory_quantity != null
      ? variant.inventory_quantity
      : (fallbackData && fallbackData.inventoryQuantity != null ? fallbackData.inventoryQuantity : (fallbackItem && fallbackItem.inventoryQuantity));
    var inventoryQuantity = inventoryQuantityRaw != null ? parseInt(inventoryQuantityRaw, 10) : null;
    var variantAvailable = variant && variant.available != null
      ? variant.available
      : parseInventoryBoolean(
          fallbackData && fallbackData.available,
          fallbackItem ? fallbackItem.variantAvailable !== false : true
        );

    if (isNaN(inventoryQuantity)) {
      inventoryQuantity = null;
    }

    if (inventoryManagement === 'null' || inventoryManagement === 'undefined') {
      inventoryManagement = '';
    }

    if (inventoryPolicy === 'null' || inventoryPolicy === 'undefined') {
      inventoryPolicy = '';
    }

    var isTracked = !!inventoryManagement;
    var canContinueSelling = inventoryPolicy === 'continue';
    var hasKnownInventory = inventoryQuantity !== null;
    var isLimited = isTracked && !canContinueSelling && hasKnownInventory;

    return {
      available: variantAvailable !== false,
      inventoryManagement: inventoryManagement,
      inventoryPolicy: inventoryPolicy,
      inventoryQuantity: inventoryQuantity,
      isTracked: isTracked,
      isLimited: isLimited,
      hasKnownInventory: hasKnownInventory,
      maxAllowed: isLimited ? Math.max(0, inventoryQuantity) : Infinity
    };
  }

  function getCurrentVariantId(card) {
    if (!card) return null;

    var singleVariant = card.querySelector('.bulk-order-single-variant');
    if (singleVariant) {
      return singleVariant.value || null;
    }

    var dropdowns = card.querySelectorAll('.bulk-order-dropdown');
    if (dropdowns.length === 0) return null;

    var selectedVariantId = null;
    var allSelected = true;

    dropdowns.forEach(function(dropdown) {
      var selectedOption = dropdown.querySelector('.bulk-order-dropdown__option.is-selected');
      if (!selectedOption) {
        allSelected = false;
        return;
      }

      selectedVariantId = selectedOption.getAttribute('data-variant-id');
    });

    return allSelected ? selectedVariantId : null;
  }

  function getStockLabel(inventoryState, threshold) {
    var lowStockThreshold = parseInt(threshold, 10);
    if (isNaN(lowStockThreshold) || lowStockThreshold < 1) {
      lowStockThreshold = 10;
    }

    if (!inventoryState.available || inventoryState.maxAllowed <= 0) {
      return {
        state: 'out_of_stock',
        text: 'Out of stock'
      };
    }

    if (inventoryState.isLimited && inventoryState.maxAllowed <= lowStockThreshold) {
      return {
        state: 'low',
        text: inventoryState.maxAllowed + ' left in stock'
      };
    }

    return {
      state: 'in_stock',
      text: 'In stock'
    };
  }

  function updateCardStockVisibility(card) {
    if (!card || card.getAttribute('data-show-stock-visibility') !== 'true') return;

    var stockStatus = card.querySelector('[data-stock-status]');
    var stockText = card.querySelector('[data-stock-text]');
    if (!stockStatus || !stockText) return;

    var selectedVariantId = getCurrentVariantId(card);
    var hasVariantDropdowns = card.querySelectorAll('.bulk-order-dropdown').length > 0;

    if (!selectedVariantId && hasVariantDropdowns) {
      stockStatus.className = 'bulk-order-stock bulk-order-stock--pending';
      stockText.textContent = 'Select options for stock';
      return;
    }

    var variant = getVariantById(card, selectedVariantId);
    var fallbackData = getSelectedVariantInventoryFallback(card, selectedVariantId);
    var inventoryState = getVariantInventoryState(variant, null, fallbackData);
    var stockLabel = getStockLabel(inventoryState, card.getAttribute('data-stock-threshold'));

    stockStatus.className = 'bulk-order-stock bulk-order-stock--' + stockLabel.state;
    stockText.textContent = stockLabel.text;
  }

  function getVariantDisplayName(productTitle, variantTitle, variant) {
    var resolvedVariantTitle = variantTitle;

    if (!resolvedVariantTitle && variant && variant.title && variant.title !== 'Default Title') {
      resolvedVariantTitle = variant.title;
    }

    if (resolvedVariantTitle) {
      return productTitle + ' (' + resolvedVariantTitle + ')';
    }

    return productTitle;
  }

  function buildInventoryError(productTitle, variantTitle, inventoryState, requestedQuantity, variant) {
    var displayName = getVariantDisplayName(productTitle, variantTitle, variant);

    if (!inventoryState.available || inventoryState.maxAllowed <= 0) {
      return {
        title: displayName,
        message: displayName + ' is out of stock.'
      };
    }

    return {
      title: displayName,
      message: 'Only ' + inventoryState.maxAllowed + ' in stock for ' + displayName + '. You selected ' + requestedQuantity + '.'
    };
  }

  function showBulkOrderValidationMessage(title, messages) {
    var list = Array.isArray(messages) ? messages : [messages];
    var filtered = list.filter(Boolean);
    if (filtered.length === 0) return;

    if (typeof Swal !== 'undefined') {
      Swal.fire({
        icon: 'warning',
        title: title,
        html: '<ul style="text-align:left;margin-top:10px;">' + filtered.map(function(message) {
          return '<li>' + escapeHtml(message) + '</li>';
        }).join('') + '</ul>',
        confirmButtonColor: '#000'
      });
      return;
    }

    alert(title + '\n\n' + filtered.join('\n'));
  }

  function validateVariantStockRequest(card, variantId, requestedQuantity, context) {
    if (!variantId || requestedQuantity <= 0) {
      return { valid: true };
    }

    var variant = getVariantById(card, variantId);
    var fallbackData = getSelectedVariantInventoryFallback(card, variantId);
    var inventoryState = getVariantInventoryState(variant, context && context.item, fallbackData);
    if (!inventoryState.available || (inventoryState.isLimited && requestedQuantity > inventoryState.maxAllowed)) {
      return {
        valid: false,
        variant: variant,
        inventoryState: inventoryState,
        error: buildInventoryError(
          (context && context.productTitle) || (context && context.item && context.item.title) || 'This item',
          (context && context.variantTitle) || (context && context.item && context.item.variantTitle) || '',
          inventoryState,
          requestedQuantity,
          variant
        )
      };
    }

    return { valid: true, variant: variant, inventoryState: inventoryState };
  }

  function validateOrderInventory() {
    var errors = [];

    BULK_ORDER.orderList.forEach(function(item) {
      var card = getProductCardByProductId(item.productId);
      var result = validateVariantStockRequest(card, item.variantId, parseInt(item.quantity, 10) || 0, {
        item: item,
        productTitle: item.title,
        variantTitle: item.variantTitle
      });

      if (!result.valid && result.error) {
        errors.push(result.error);
      }
    });

    return {
      valid: errors.length === 0,
      errors: errors
    };
  }

  function syncOrderItemInventoryState(productId, variantId, inventoryState) {
    if (!inventoryState) return;

    var itemIndex = BULK_ORDER.findItemIndex(productId, variantId);
    if (itemIndex === -1) return;

    BULK_ORDER.orderList[itemIndex].variantAvailable = inventoryState.available;
    BULK_ORDER.orderList[itemIndex].inventoryManagement = inventoryState.inventoryManagement;
    BULK_ORDER.orderList[itemIndex].inventoryPolicy = inventoryState.inventoryPolicy;
    BULK_ORDER.orderList[itemIndex].inventoryQuantity = inventoryState.inventoryQuantity;
  }

  /**
   * Filter available option values based on currently selected options
   * @param {Array} variants - All product variants
   * @param {number} optionIndex - The index of the option being filtered
   * @param {Array} selectedOptions - Array of selected values [{index: 0, value: 'Standard'}]
   * @returns {Array} - Array of available values for the option at optionIndex
   */
  function getAvailableOptionValues(variants, optionIndex, selectedOptions) {
    var availableValues = [];
    
    variants.forEach(function(variant) {
      // Check if this variant matches all previously selected options
      var matchesPreviousSelections = true;
      
      selectedOptions.forEach(function(selection) {
        // Only check options that come before the current option
        if (selection.index < optionIndex) {
          if (variant.options[selection.index] !== selection.value) {
            matchesPreviousSelections = false;
          }
        }
      });
      
      if (matchesPreviousSelections) {
        var optionValue = variant.options[optionIndex];
        if (optionValue && availableValues.indexOf(optionValue) === -1) {
          availableValues.push(optionValue);
        }
      }
    });
    
    return availableValues;
  }

  /**
   * Find the correct variant for given option selections
   */
  function findMatchingVariant(variants, selectedOptions, optionIndex, optionValue) {
    for (var i = 0; i < variants.length; i++) {
      var variant = variants[i];
      var matches = true;
      
      // Check all previous selections
      selectedOptions.forEach(function(selection) {
        if (selection.index < optionIndex && variant.options[selection.index] !== selection.value) {
          matches = false;
        }
      });
      
      // Check current option
      if (matches && variant.options[optionIndex] === optionValue) {
        return variant;
      }
    }
    return null;
  }

  /**
   * Update dropdown options visibility based on current selections
   */
  function updateDropdownOptions(card) {
    var variants = getProductVariants(card);
    if (variants.length === 0) return;
    
    var dropdowns = card.querySelectorAll('.bulk-order-dropdown');
    
    // Get currently selected options
    var selectedOptions = [];
    dropdowns.forEach(function(dropdown, index) {
      var selectedOption = dropdown.querySelector('.bulk-order-dropdown__option.is-selected');
      if (selectedOption) {
        selectedOptions.push({
          index: index,
          value: selectedOption.getAttribute('data-value')
        });
      }
    });
    
    // Update each dropdown (except the first one which always shows all)
    dropdowns.forEach(function(dropdown, dropdownIndex) {
      if (dropdownIndex === 0) return; // First option always shows all
      
      var availableValues = getAvailableOptionValues(variants, dropdownIndex, selectedOptions);
      var options = dropdown.querySelectorAll('.bulk-order-dropdown__option');
      
      options.forEach(function(option) {
        var value = option.getAttribute('data-value');
        
        if (availableValues.indexOf(value) === -1) {
          // Hide option that's not available with current selections
          option.classList.add('is-hidden');
          option.style.display = 'none';
        } else {
          // Show option and update variant info
          option.classList.remove('is-hidden');
          option.style.display = '';
          
          // Find the matching variant for this option value with current selections
          var matchingVariant = findMatchingVariant(variants, selectedOptions, dropdownIndex, value);
          if (matchingVariant) {
            option.setAttribute('data-variant-id', matchingVariant.id);
            option.setAttribute('data-price', matchingVariant.price);
            option.setAttribute('data-variant-available', matchingVariant.available);
            option.setAttribute('data-variant-inventory-management', matchingVariant.inventory_management || '');
            option.setAttribute('data-variant-inventory-policy', matchingVariant.inventory_policy || '');
            option.setAttribute(
              'data-variant-inventory-quantity',
              matchingVariant.inventory_quantity != null ? matchingVariant.inventory_quantity : ''
            );
            
            // Update availability based on the matched variant
            if (matchingVariant.available) {
              option.classList.remove('is-unavailable');
              option.removeAttribute('aria-disabled');
              var statusEl = option.querySelector('.bulk-order-dropdown__option-status');
              if (statusEl) statusEl.style.display = 'none';
            } else {
              option.classList.add('is-unavailable');
              option.setAttribute('aria-disabled', 'true');
              var statusEl = option.querySelector('.bulk-order-dropdown__option-status');
              if (statusEl) statusEl.style.display = '';
            }
            
            // Update image if variant has featured image
            var swatchEl = option.querySelector('.bulk-order-dropdown__option-swatch');
            var tooltipImg = option.querySelector('.bulk-order-dropdown__tooltip img');
            var previewImg = option.querySelector('.bulk-order-dropdown__option-preview img');
            
            if (matchingVariant.featured_image) {
              var imgUrl = matchingVariant.featured_image.src;
              if (swatchEl) {
                swatchEl.style.backgroundImage = "url('" + imgUrl.replace(/\.([^\.]+)$/, '_48x48.$1') + "')";
              }
              if (tooltipImg) {
                tooltipImg.src = imgUrl.replace(/\.([^\.]+)$/, '_240x240.$1');
              }
              if (previewImg) {
                previewImg.src = imgUrl.replace(/\.([^\.]+)$/, '_300x300.$1');
              }
            }
          }
        }
      });
      
      // If currently selected option is now hidden, deselect it
      var currentlySelected = dropdown.querySelector('.bulk-order-dropdown__option.is-selected');
      if (currentlySelected && currentlySelected.classList.contains('is-hidden')) {
        currentlySelected.classList.remove('is-selected');
        var trigger = dropdown.querySelector('.bulk-order-dropdown__trigger');
        var optionName = dropdown.getAttribute('data-option-name') || 'Select';
        if (trigger) {
          trigger.querySelector('.bulk-order-dropdown__text').textContent = optionName;
          trigger.setAttribute('data-selected-variant', '');
          trigger.setAttribute('data-selected-price', '');
        }
      }
    });
  }

  function initDropdowns() {
    document.querySelectorAll('.bulk-order-dropdown').forEach(function(dropdown) {
      var trigger = dropdown.querySelector('.bulk-order-dropdown__trigger');
      var menu = dropdown.querySelector('.bulk-order-dropdown__menu');
      var options = dropdown.querySelectorAll('.bulk-order-dropdown__option');
      
      if (!trigger || !menu) return;

      // Toggle dropdown
      trigger.addEventListener('click', function(e) {
        e.preventDefault();
        var isOpen = menu.classList.contains('is-open');
        
        // Close all other dropdowns
        document.querySelectorAll('.bulk-order-dropdown.is-open').forEach(function(d) {
          d.classList.remove('is-open');
          var m = d.querySelector('.bulk-order-dropdown__menu');
          if (m) m.classList.remove('is-open');
          var t = d.querySelector('.bulk-order-dropdown__trigger');
          if (t) t.setAttribute('aria-expanded', 'false');
        });

        if (!isOpen) {
          dropdown.classList.add('is-open');
          menu.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });

      // Select option
      options.forEach(function(option) {
        option.addEventListener('click', function(e) {
          // Don't allow selecting hidden or unavailable options
          if (option.classList.contains('is-hidden')) return;
          if (option.classList.contains('is-unavailable')) return;

          var value = option.getAttribute('data-value');
          var text = option.querySelector('.bulk-order-dropdown__option-text').textContent;
          var variantId = option.getAttribute('data-variant-id');
          var price = option.getAttribute('data-price');

          // Update trigger
          trigger.querySelector('.bulk-order-dropdown__text').textContent = text;
          trigger.setAttribute('data-selected-variant', variantId);
          trigger.setAttribute('data-selected-price', price);

          // Update selected state
          options.forEach(function(o) { o.classList.remove('is-selected'); });
          option.classList.add('is-selected');

          // Close menu
          dropdown.classList.remove('is-open');
          menu.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');

          // Update cascading dropdowns
          var card = dropdown.closest('.bulk-order-product-card');
          if (card) {
            updateDropdownOptions(card);
            
            // Update selection row with selected variant info
            updateSelectionRow(card);
            updateCardStockVisibility(card);
            
            // Update the Add button state (multi-variant mode)
            updateAddVariantButtonState(card);
          }

          // Trigger change event
          dropdown.dispatchEvent(new CustomEvent('variantChange', {
            detail: { value: value, variantId: variantId, price: price }
          }));
        });

        // Mobile: tap to expand image preview
        if (window.innerWidth <= 768 && option.classList.contains('has-image')) {
          option.addEventListener('click', function(e) {
            if (e.target.closest('.bulk-order-dropdown__option-preview')) return;
            option.classList.toggle('is-expanded');
            e.stopPropagation();
          });
        }
      });

      // Close on outside click
      document.addEventListener('click', function(e) {
        if (!dropdown.contains(e.target)) {
          dropdown.classList.remove('is-open');
          menu.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
        }
      });

      // Keyboard navigation
      trigger.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          trigger.click();
        } else if (e.key === 'Escape') {
          dropdown.classList.remove('is-open');
          menu.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }

  // ==========================================================================
  // Desktop Variant Image Tooltips
  // ==========================================================================

  function initVariantImageTooltips() {
    // Only on desktop
    if (window.innerWidth <= 768) return;

    // Create shared tooltip element
    var tooltip = document.getElementById('bulk-order-variant-tooltip');
    if (!tooltip) {
      tooltip = document.createElement('div');
      tooltip.id = 'bulk-order-variant-tooltip';
      tooltip.className = 'bulk-order-dropdown__tooltip';
      tooltip.innerHTML = '<img src="" alt="" loading="lazy">';
      document.body.appendChild(tooltip);
    }
    var tooltipImg = tooltip.querySelector('img');

    // Handle mouseenter on options with images
    document.querySelectorAll('.bulk-order-dropdown__option[data-variant-image]').forEach(function(option) {
      option.addEventListener('mouseenter', function() {
        var imageUrl = option.getAttribute('data-variant-image');
        if (!imageUrl) return;

        // Set image source
        tooltipImg.src = imageUrl;
        tooltipImg.alt = option.querySelector('.bulk-order-dropdown__option-text')?.textContent || '';

        // Get option position
        var rect = option.getBoundingClientRect();
        var menu = option.closest('.bulk-order-dropdown__menu');
        var menuRect = menu ? menu.getBoundingClientRect() : rect;

        // Position tooltip to the right of the dropdown menu
        var tooltipWidth = 140;
        var tooltipHeight = 140;
        var gap = 12;

        // Try positioning to the right of the menu
        var left = menuRect.right + gap;
        var top = rect.top + (rect.height / 2) - (tooltipHeight / 2);

        // If tooltip goes off right edge, position to the left
        if (left + tooltipWidth > window.innerWidth - 16) {
          left = menuRect.left - tooltipWidth - gap;
        }

        // Keep tooltip in viewport vertically
        if (top < 8) top = 8;
        if (top + tooltipHeight > window.innerHeight - 8) {
          top = window.innerHeight - tooltipHeight - 8;
        }

        tooltip.style.left = left + 'px';
        tooltip.style.top = top + 'px';
        tooltip.classList.add('is-visible');
      });

      option.addEventListener('mouseleave', function() {
        tooltip.classList.remove('is-visible');
      });
    });

    // Hide tooltip when dropdown closes
    document.querySelectorAll('.bulk-order-dropdown__menu').forEach(function(menu) {
      var observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            if (!menu.classList.contains('is-open')) {
              tooltip.classList.remove('is-visible');
            }
          }
        });
      });
      observer.observe(menu, { attributes: true });
    });
  }

  // ==========================================================================
  // Quantity Controls (Multi-variant: Requires explicit "Add" button click)
  // ==========================================================================

  function initQuantityControls() {
    document.querySelectorAll('.bulk-order-quantity').forEach(function(qty) {
      var input = qty.querySelector('.bulk-order-quantity__input');
      var minusBtn = qty.querySelector('[data-action="minus"]');
      var plusBtn = qty.querySelector('[data-action="plus"]');

      if (!input) return;

      function updateValue(delta) {
        var current = parseInt(input.value) || 0;
        var newValue = Math.max(0, current + delta);
        input.value = newValue;
        if (minusBtn) minusBtn.disabled = newValue <= 0;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (minusBtn) {
        minusBtn.addEventListener('click', function() { updateValue(-1); });
      }
      if (plusBtn) {
        plusBtn.addEventListener('click', function() { updateValue(1); });
      }

      // Update add button state on quantity change
      input.addEventListener('change', function() {
        var value = parseInt(input.value) || 0;
        if (value < 0) {
          input.value = 0;
          value = 0;
        }
        if (minusBtn) minusBtn.disabled = value <= 0;
        
        // Update the Add button state
        var card = qty.closest('.bulk-order-product-card');
        if (card) {
          updateSelectionRow(card);
          updateAddVariantButtonState(card);
        }
      });
    });
  }

  // ==========================================================================
  // Multi-Variant Selection ("Add" Button)
  // ==========================================================================

  function initAddVariantButtons() {
    document.querySelectorAll('[data-add-variant]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var card = btn.closest('.bulk-order-product-card');
        if (!card) return;

        var qtyInput = card.querySelector('.bulk-order-quantity__input');
        var quantity = qtyInput ? parseInt(qtyInput.value) || 0 : 0;

        if (quantity <= 0) return;

        // Add the variant to order
        addVariantFromCard(card, quantity);

        // Reset quantity to 0 for next selection
        if (qtyInput) {
          qtyInput.value = 0;
          var minusBtn = card.querySelector('[data-action="minus"]');
          if (minusBtn) minusBtn.disabled = true;
        }

        updateSelectionRow(card);

        // Update button state
        updateAddVariantButtonState(card);

        // Update the added variants display
        renderAddedVariantsInCard(card);
      });
    });
  }

  // Update the Add button enabled/disabled state
  function updateAddVariantButtonState(card) {
    var addBtn = card.querySelector('[data-add-variant]');
    if (!addBtn) return;

    var qtyInput = card.querySelector('.bulk-order-quantity__input');
    var quantity = qtyInput ? parseInt(qtyInput.value) || 0 : 0;

    // Check if variant is selected
    var allSelected = isVariantSelected(card);

    // Enable button if variant is selected and quantity > 0
    addBtn.disabled = !allSelected || quantity <= 0;
  }

  // Check if a variant is fully selected
  function isVariantSelected(card) {
    var dropdowns = card.querySelectorAll('.bulk-order-dropdown');
    var singleVariant = card.querySelector('.bulk-order-single-variant');

    if (singleVariant) return true;

    if (dropdowns.length === 0) return false;

    var allSelected = true;
    dropdowns.forEach(function(dropdown) {
      var selectedOption = dropdown.querySelector('.bulk-order-dropdown__option.is-selected');
      if (!selectedOption) {
        allSelected = false;
      }
    });

    return allSelected;
  }

  // Add variant from card to order
  function addVariantFromCard(card, quantity) {
    var productId = card.getAttribute('data-product-id');
    var productTitle = card.getAttribute('data-product-title');
    var productHandle = card.getAttribute('data-product-handle');
    var productImage = card.getAttribute('data-product-image');
    var discountTiers = card.getAttribute('data-discount-tiers') || '';
    var discountSource = card.getAttribute('data-discount-source') || '';

    var selectedOptions = [];
    var variantId = null;
    var price = 0;

    var dropdowns = card.querySelectorAll('.bulk-order-dropdown');
    dropdowns.forEach(function(dropdown) {
      var selectedOption = dropdown.querySelector('.bulk-order-dropdown__option.is-selected');
      if (selectedOption) {
        selectedOptions.push(selectedOption.getAttribute('data-value'));
        variantId = selectedOption.getAttribute('data-variant-id');
        price = parseInt(selectedOption.getAttribute('data-price')) || 0;
      }
    });

    var singleVariant = card.querySelector('.bulk-order-single-variant');
    if (singleVariant) {
      variantId = singleVariant.value;
      price = parseInt(singleVariant.getAttribute('data-price')) || 0;
    }

    if (!variantId) return;

    var variantTitle = selectedOptions.length > 0 ? selectedOptions.join(' / ') : '';
    var existingIndex = BULK_ORDER.findItemIndex(productId, variantId);
    var existingQty = existingIndex > -1 ? BULK_ORDER.orderList[existingIndex].quantity : 0;
    var stockValidation = validateVariantStockRequest(card, variantId, existingQty + quantity, {
      productTitle: productTitle,
      variantTitle: variantTitle
    });

    if (!stockValidation.valid) {
      showBulkOrderValidationMessage('Not Enough Stock', stockValidation.error.message);
      return;
    }

    // Check if this variant is already in the order
    if (existingIndex > -1) {
      // Update quantity (add to existing)
      BULK_ORDER.updateQuantity(productId, variantId, existingQty + quantity);
      syncOrderItemInventoryState(productId, variantId, stockValidation.inventoryState);
    } else {
      // Add new item
      BULK_ORDER.addItem({
        productId: productId,
        variantId: variantId,
        quantity: quantity,
        title: productTitle,
        variantTitle: variantTitle,
        price: price,
        basePrice: price,
        discountTiers: discountTiers,
        discountSource: discountSource,
        image: productImage,
        handle: productHandle,
        variantAvailable: stockValidation.inventoryState.available,
        inventoryManagement: stockValidation.inventoryState.inventoryManagement,
        inventoryPolicy: stockValidation.inventoryState.inventoryPolicy,
        inventoryQuantity: stockValidation.inventoryState.inventoryQuantity
      });
    }

    // Update card state
    card.classList.add('is-in-order');
  }

  // Render the list of added variants within a product card
  function renderAddedVariantsInCard(card) {
    var productId = card.getAttribute('data-product-id');
    var container = card.querySelector('[data-added-variants]');
    var list = card.querySelector('[data-added-variants-list]');

    if (!container || !list) return;

    // Find all items for this product
    var items = BULK_ORDER.orderList.filter(function(item) {
      return item.productId == productId;
    });

    if (items.length === 0) {
      container.hidden = true;
      card.classList.remove('is-in-order');
      return;
    }

    container.hidden = false;
    card.classList.add('is-in-order');

    // Render items
    list.innerHTML = items.map(function(item) {
      var displayName = item.variantTitle || item.title;
      var ariaLabel = item.variantTitle ? 'Remove ' + escapeHtml(item.variantTitle) : 'Remove item';
      return '<li class="bulk-order-added-variant-item" data-variant-id="' + item.variantId + '">' +
        '<span class="bulk-order-added-variant-item__name">' + escapeHtml(displayName) + '</span>' +
        '<span class="bulk-order-added-variant-item__qty">×' + item.quantity + '</span>' +
        '<span class="bulk-order-added-variant-item__price">' + BULK_ORDER.formatMoney(BULK_ORDER.getItemLinePrice(item)) + '</span>' +
        '<button type="button" class="bulk-order-added-variant-item__remove" data-remove-variant="' + item.variantId + '" aria-label="' + ariaLabel + '">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
            '<line x1="18" y1="6" x2="6" y2="18"></line>' +
            '<line x1="6" y1="6" x2="18" y2="18"></line>' +
          '</svg>' +
        '</button>' +
      '</li>';
    }).join('');

    // Add remove handlers
    list.querySelectorAll('[data-remove-variant]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        var variantId = btn.getAttribute('data-remove-variant');
        BULK_ORDER.removeItem(productId, variantId);
        renderAddedVariantsInCard(card);
      });
    });
  }

  // Escape HTML to prevent XSS
  function escapeHtml(text) {
    var div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Render added variants for all cards (on page load)
  function renderAllAddedVariants() {
    document.querySelectorAll('.bulk-order-product-card').forEach(function(card) {
      renderAddedVariantsInCard(card);
      updateAddVariantButtonState(card);
      updateCardStockVisibility(card);
    });
  }

  // Update selection row display
  function updateSelectionRow(card) {
    var selectionRow = card.querySelector('.bulk-order-selection-row');
    if (!selectionRow) return;

    var selectedOptions = [];
    var basePrice = 0;
    var selectedVariantId = null;

    card.querySelectorAll('.bulk-order-dropdown').forEach(function(dropdown) {
      var selectedOption = dropdown.querySelector('.bulk-order-dropdown__option.is-selected');
      if (selectedOption) {
        selectedOptions.push(selectedOption.getAttribute('data-value'));
        basePrice = parseInt(selectedOption.getAttribute('data-price'), 10) || 0;
        selectedVariantId = selectedOption.getAttribute('data-variant-id');
      }
    });

    var singleVariant = card.querySelector('.bulk-order-single-variant');
    if (singleVariant) {
      basePrice = parseInt(singleVariant.getAttribute('data-price'), 10) || 0;
      selectedVariantId = singleVariant.value;
    }

    var productId = card.getAttribute('data-product-id');
    var qtyInput = card.querySelector('.bulk-order-quantity__input');
    var pendingQuantity = qtyInput ? (parseInt(qtyInput.value, 10) || 0) : 0;
    var existingIndex = selectedVariantId ? BULK_ORDER.findItemIndex(productId, selectedVariantId) : -1;
    var existingLineQuantity = existingIndex > -1 ? parseInt(BULK_ORDER.orderList[existingIndex].quantity, 10) || 0 : 0;
    var effectivePrice = BULK_ORDER.getEffectiveUnitPrice(productId, basePrice, {
      card: card,
      productQuantity: BULK_ORDER.getProductQuantity(productId) + pendingQuantity,
      lineQuantity: existingLineQuantity + pendingQuantity,
      discountTiersRaw: card.getAttribute('data-discount-tiers') || ''
    });

    var variantEl = selectionRow.querySelector('.bulk-order-selection-row__variant');
    var priceEl = selectionRow.querySelector('.bulk-order-selection-row__price');

    if (selectedOptions.length > 0) {
      if (variantEl) variantEl.textContent = selectedOptions.join(' / ');
    } else if (singleVariant) {
      if (variantEl) variantEl.textContent = '';
    }

    if (priceEl && effectivePrice > 0) {
      priceEl.textContent = BULK_ORDER.formatMoney(effectivePrice);
    }
  }

  // Update order based on card state
  function updateOrderFromCard(card, quantity) {
    var productId = card.getAttribute('data-product-id');
    var productTitle = card.getAttribute('data-product-title');
    var productHandle = card.getAttribute('data-product-handle');
    var productImage = card.getAttribute('data-product-image');
    var discountTiers = card.getAttribute('data-discount-tiers') || '';
    var discountSource = card.getAttribute('data-discount-source') || '';

    // Get selected variant from dropdowns
    var selectedOptions = [];
    var variantId = null;
    var variantTitle = '';
    var price = 0;

    var dropdowns = card.querySelectorAll('.bulk-order-dropdown');
    var allSelected = true;

    dropdowns.forEach(function(dropdown) {
      var selectedOption = dropdown.querySelector('.bulk-order-dropdown__option.is-selected');
      if (selectedOption) {
        selectedOptions.push(selectedOption.getAttribute('data-value'));
        variantId = selectedOption.getAttribute('data-variant-id');
        price = parseInt(selectedOption.getAttribute('data-price')) || 0;
      } else {
        allSelected = false;
      }
    });

    // For single-variant products
    var singleVariant = card.querySelector('.bulk-order-single-variant');
    if (singleVariant) {
      variantId = singleVariant.value;
      price = parseInt(singleVariant.getAttribute('data-price')) || 0;
      allSelected = true;
    }

    if (!allSelected || !variantId) {
      // Can't add to order without selecting all options
      return;
    }

    variantTitle = selectedOptions.length > 0 ? selectedOptions.join(' / ') : '';

    // Update selection row display
    var selectionRow = card.querySelector('.bulk-order-selection-row');
    if (selectionRow) {
      var variantEl = selectionRow.querySelector('.bulk-order-selection-row__variant');
      var priceEl = selectionRow.querySelector('.bulk-order-selection-row__price');
      if (variantEl) variantEl.textContent = variantTitle;
      if (priceEl) priceEl.textContent = BULK_ORDER.formatMoney(price);
    }

    if (quantity === 0) {
      // Remove from order
      BULK_ORDER.removeItem(productId, variantId);
    } else {
      var stockValidation = validateVariantStockRequest(card, variantId, quantity, {
        productTitle: productTitle,
        variantTitle: variantTitle
      });

      if (!stockValidation.valid) {
        showBulkOrderValidationMessage('Not Enough Stock', stockValidation.error.message);
        return;
      }

      // Add or update in order
      var existingIndex = BULK_ORDER.findItemIndex(productId, variantId);
      if (existingIndex > -1) {
        BULK_ORDER.updateQuantity(productId, variantId, quantity);
        syncOrderItemInventoryState(productId, variantId, stockValidation.inventoryState);
      } else {
        BULK_ORDER.addItem({
          productId: productId,
          variantId: variantId,
          quantity: quantity,
          title: productTitle,
          variantTitle: variantTitle,
          price: price,
          basePrice: price,
          discountTiers: discountTiers,
          discountSource: discountSource,
          image: productImage,
          handle: productHandle,
          variantAvailable: stockValidation.inventoryState.available,
          inventoryManagement: stockValidation.inventoryState.inventoryManagement,
          inventoryPolicy: stockValidation.inventoryState.inventoryPolicy,
          inventoryQuantity: stockValidation.inventoryState.inventoryQuantity
        });
      }
    }
  }

  // ==========================================================================
  // Add to Order Buttons
  // ==========================================================================

  function initAddButtons() {
    document.querySelectorAll('.bulk-order-add-btn').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        
        var card = btn.closest('.bulk-order-product-card');
        if (!card) return;

        var productId = card.getAttribute('data-product-id');
        var productTitle = card.getAttribute('data-product-title');
        var productHandle = card.getAttribute('data-product-handle');
        var productImage = card.getAttribute('data-product-image');
        var discountSource = card.getAttribute('data-discount-source') || '';

        var dropdown = card.querySelector('.bulk-order-dropdown__trigger');
        var qtyInput = card.querySelector('.bulk-order-quantity__input');

        var variantId = dropdown ? dropdown.getAttribute('data-selected-variant') : null;
        var variantTitle = dropdown ? dropdown.querySelector('.bulk-order-dropdown__text').textContent : '';
        var price = dropdown ? parseInt(dropdown.getAttribute('data-selected-price')) : 0;
        var quantity = qtyInput ? parseInt(qtyInput.value) || 1 : 1;

        if (!variantId) {
          // Show error - need to select variant first
          btn.classList.add('is-error');
          setTimeout(function() { btn.classList.remove('is-error'); }, 1000);
          return;
        }

        var existingIndex = BULK_ORDER.findItemIndex(productId, variantId);
        var existingQty = existingIndex > -1 ? BULK_ORDER.orderList[existingIndex].quantity : 0;
        var stockValidation = validateVariantStockRequest(card, variantId, existingQty + quantity, {
          productTitle: productTitle,
          variantTitle: variantTitle
        });

        if (!stockValidation.valid) {
          showBulkOrderValidationMessage('Not Enough Stock', stockValidation.error.message);
          return;
        }

        BULK_ORDER.addItem({
          productId: productId,
          variantId: variantId,
          quantity: quantity,
          title: productTitle,
          variantTitle: variantTitle,
          price: price,
          basePrice: price,
          discountTiers: card.getAttribute('data-discount-tiers') || '',
          discountSource: discountSource,
          image: productImage,
          handle: productHandle,
          variantAvailable: stockValidation.inventoryState.available,
          inventoryManagement: stockValidation.inventoryState.inventoryManagement,
          inventoryPolicy: stockValidation.inventoryState.inventoryPolicy,
          inventoryQuantity: stockValidation.inventoryState.inventoryQuantity
        });
        syncOrderItemInventoryState(productId, variantId, stockValidation.inventoryState);

        // Reset quantity
        if (qtyInput) qtyInput.value = 1;

        // Visual feedback
        btn.textContent = 'Added!';
        setTimeout(function() {
          btn.textContent = BULK_ORDER.isInOrder(productId, variantId) ? 'Update Order' : 'Add to Order';
        }, 1000);
      });
    });
  }

  // ==========================================================================
  // Sidebar Remove Buttons
  // ==========================================================================

  function initSidebarRemove() {
    document.addEventListener('click', function(e) {
      var removeBtn = e.target.closest('.bulk-order-sidebar__item-remove');
      if (!removeBtn) return;

      var item = removeBtn.closest('.bulk-order-sidebar__item');
      if (!item) return;

      var productId = item.getAttribute('data-product-id');
      var variantId = item.getAttribute('data-variant-id');

      BULK_ORDER.removeItem(productId, variantId);
    });
  }

  // ==========================================================================
  // Sidebar Toggle (Desktop + Mobile)
  // ==========================================================================

  function initMobileSidebar() {
    var sidebar = document.querySelector('.bulk-order-sidebar');
    var toggle = document.querySelector('.bulk-order-sidebar__toggle');
    var header = document.querySelector('.bulk-order-sidebar__header');

    if (!sidebar) return;

    // Toggle via header click (works on both desktop and mobile)
    if (header) {
      header.addEventListener('click', function() {
        // Only allow expanding if there are items
        if (sidebar.classList.contains('has-items')) {
          sidebar.classList.toggle('is-expanded');
        }
      });
    }

    // Toggle via mobile drag handle
    if (toggle) {
      toggle.addEventListener('click', function() {
        sidebar.classList.toggle('is-expanded');
      });
    }

    // Swipe to close
    var startY = 0;
    sidebar.addEventListener('touchstart', function(e) {
      startY = e.touches[0].clientY;
    });

    sidebar.addEventListener('touchmove', function(e) {
      var deltaY = e.touches[0].clientY - startY;
      if (deltaY > 50 && sidebar.classList.contains('is-expanded')) {
        sidebar.classList.remove('is-expanded');
      }
    });
  }

  // ==========================================================================
  // Step 2: Per-Product Notes
  // ==========================================================================

  function initPerProductNotes() {
    var container = document.getElementById('BulkOrderPerProductNotes');
    if (!container) return;

    // Render notes for each order item
    container.innerHTML = BULK_ORDER.orderList.map(function(item) {
      var note = BULK_ORDER.getProductNote(item.productId, item.variantId);
      var variantInfo = item.variantTitle ? BULK_ORDER.escapeHtml(item.variantTitle) + ' × ' + item.quantity : 'Qty: ' + item.quantity;
      return '<div class="bulk-order-per-product-note">' +
        '<div class="bulk-order-per-product-note__header">' +
          '<div class="bulk-order-per-product-note__image">' +
            (item.image ? '<img src="' + item.image + '&width=80" alt="">' : '') +
          '</div>' +
          '<div class="bulk-order-per-product-note__info">' +
            '<p class="bulk-order-per-product-note__name">' + BULK_ORDER.escapeHtml(item.title) + '</p>' +
            '<p class="bulk-order-per-product-note__variant">' + variantInfo + '</p>' +
          '</div>' +
        '</div>' +
        '<textarea class="bulk-order-per-product-note__textarea" ' +
          'data-product-id="' + item.productId + '" ' +
          'data-variant-id="' + item.variantId + '" ' +
          'placeholder="' + (window.BULK_ORDER_SETTINGS?.perProductNotesLabel || 'Additional notes for this item...') + '">' +
          BULK_ORDER.escapeHtml(note) +
        '</textarea>' +
      '</div>';
    }).join('');

    // Bind change events
    container.querySelectorAll('.bulk-order-per-product-note__textarea').forEach(function(textarea) {
      textarea.addEventListener('change', function() {
        var productId = textarea.getAttribute('data-product-id');
        var variantId = textarea.getAttribute('data-variant-id');
        BULK_ORDER.setProductNote(productId, variantId, textarea.value);
      });
    });
  }

  // ==========================================================================
  // Step 3: Review & Checkout
  // ==========================================================================

  function initReviewStep() {
    var reviewContainer = document.querySelector('.bulk-order-review__items');
    if (!reviewContainer) return;

    // Render review items
    reviewContainer.innerHTML = BULK_ORDER.orderList.map(function(item) {
      var note = BULK_ORDER.getProductNote(item.productId, item.variantId);
      return '<div class="bulk-order-review__item" data-product-id="' + item.productId + '" data-variant-id="' + item.variantId + '">' +
        '<div class="bulk-order-review__item-image">' +
          (item.image ? '<img src="' + item.image + '&width=160" alt="">' : '') +
        '</div>' +
        '<div class="bulk-order-review__item-info">' +
          '<h4 class="bulk-order-review__item-title">' + BULK_ORDER.escapeHtml(item.title) + '</h4>' +
          (item.variantTitle ? '<p class="bulk-order-review__item-variant">' + BULK_ORDER.escapeHtml(item.variantTitle) + '</p>' : '') +
          '<p class="bulk-order-review__item-qty">Quantity: ' + item.quantity + '</p>' +
          (note ? '<div class="bulk-order-review__item-note">Note: ' + BULK_ORDER.escapeHtml(note) + '</div>' : '') +
        '</div>' +
        '<div class="bulk-order-review__item-price">' + BULK_ORDER.formatMoney(BULK_ORDER.getItemLinePrice(item)) + '</div>' +
      '</div>';
    }).join('');

    // Update pricing
    updateReviewPricing();

    // Render customizations
    renderCustomizations();
  }

  function updateReviewPricing() {
    var subtotalEl = document.querySelector('.bulk-order-review__pricing-subtotal');
    var totalEl = document.querySelector('.bulk-order-review__pricing-total-value');
    var itemCountEl = document.querySelector('.bulk-order-review__pricing-items');

    var total = BULK_ORDER.getTotalPrice();
    var itemCount = BULK_ORDER.getTotalItems();

    if (subtotalEl) subtotalEl.textContent = BULK_ORDER.formatMoney(total);
    if (totalEl) totalEl.textContent = BULK_ORDER.formatMoney(total);
    if (itemCountEl) itemCountEl.textContent = itemCount + ' item' + (itemCount !== 1 ? 's' : '');
  }

  function renderCustomizations() {
    var container = document.querySelector('.bulk-order-review__customizations');
    if (!container) return;

    var customizations = BULK_ORDER.customizations;
    var keys = Object.keys(customizations);

    if (keys.length === 0) {
      container.style.display = 'none';
      return;
    }

    container.style.display = 'block';
    var itemsHtml = keys.map(function(key) {
      var value = customizations[key];
      if (typeof value === 'boolean') {
        value = value ? 'Yes' : 'No';
      }
      return '<div class="bulk-order-review__customization-item">' +
        '<span class="bulk-order-review__customization-label">' + BULK_ORDER.escapeHtml(key) + ':</span>' +
        '<span class="bulk-order-review__customization-value">' + BULK_ORDER.escapeHtml(String(value)) + '</span>' +
      '</div>';
    }).join('');

    var itemsContainer = container.querySelector('.bulk-order-review__customization-items');
    if (itemsContainer) {
      itemsContainer.innerHTML = itemsHtml;
    }
  }

  // Checkout button
  function initCheckoutButton() {
    var checkoutBtn = document.querySelector('.bulk-order-checkout-btn');
    if (!checkoutBtn) return;

    checkoutBtn.addEventListener('click', function(e) {
      e.preventDefault();
      var stockValidation = validateOrderInventory();
      if (!stockValidation.valid) {
        showBulkOrderValidationMessage('Not Enough Stock', stockValidation.errors.map(function(error) {
          return error.message;
        }));
        return;
      }

      checkoutBtn.disabled = true;
      checkoutBtn.textContent = 'Adding to cart...';

      BULK_ORDER.addToCart()
        .catch(function(error) {
          console.error('Checkout failed:', error);
          checkoutBtn.disabled = false;
          checkoutBtn.textContent = window.BULK_ORDER_SETTINGS?.checkoutButtonText || 'Checkout Now';

          showBulkOrderValidationMessage(
            'Unable To Add Items',
            error && error.message ? error.message : 'There was an error adding items to your cart. Please try again.'
          );
        });
    });
  }

  // ==========================================================================
  // Step Detection and Navigation
  // ==========================================================================

  function getCurrentStep() {
    var urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('step') || '1';
  }

  function updateProgressBar(activeStep) {
    var progressSteps = document.querySelectorAll('.bulk-order-progress__step');
    var progressLabels = document.querySelectorAll('.bulk-order-progress__label');
    var progressWrappers = document.querySelectorAll('.bulk-order-progress__step-wrapper');
    var connectors = document.querySelectorAll('.bulk-order-progress__connector');
    
    progressSteps.forEach(function(step, index) {
      var stepNum = (index + 1).toString();
      step.classList.remove('is-active', 'is-completed');
      if (progressLabels[index]) progressLabels[index].classList.remove('is-active');
      if (progressWrappers[index]) progressWrappers[index].classList.remove('is-completed', 'is-current-mobile');
      
      if (parseInt(stepNum) < parseInt(activeStep)) {
        step.classList.add('is-completed');
        if (progressWrappers[index]) progressWrappers[index].classList.add('is-completed');
        // Update step content to checkmark
        step.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"></polyline></svg>';
      } else if (stepNum === activeStep) {
        step.classList.add('is-active');
        if (progressLabels[index]) progressLabels[index].classList.add('is-active');
        if (progressWrappers[index]) progressWrappers[index].classList.add('is-current-mobile');
        // Reset step content to number
        step.textContent = stepNum;
      } else {
        // Future steps - just show number
        step.textContent = stepNum;
      }
    });

    // Update connectors
    connectors.forEach(function(connector, index) {
      connector.classList.remove('is-completed');
      if (parseInt(activeStep) > (index + 1)) {
        connector.classList.add('is-completed');
      }
    });
  }

  function showStep(stepNum) {
    var settings = window.BULK_ORDER_SETTINGS || {};
    var pageUrl = settings.pageUrl || '/pages/bulk-order';
    var inDesignMode = isDesignMode();
    
    // Update data-step attribute
    var bulkOrder = document.getElementById('bulk-order');
    if (bulkOrder) {
      bulkOrder.setAttribute('data-step', stepNum);
    }

    // Update progress bar
    updateProgressBar(stepNum);

    // Hide all step content
    var step1Content = document.querySelector('.bulk-order-step-1');
    var step2Content = document.querySelector('.bulk-order-step-2');
    var step3Content = document.querySelector('.bulk-order-step-3');
    
    if (step1Content) step1Content.style.display = 'none';
    if (step2Content) step2Content.style.display = 'none';
    if (step3Content) step3Content.style.display = 'none';

    // Show active step
    if (stepNum === '1' && step1Content) {
      step1Content.style.display = 'block';
    } else if (stepNum === '2') {
      loadStep2Content();
    } else if (stepNum === '3') {
      loadStep3Content();
    }

    // Update navigation buttons
    updateNavigationButtons(stepNum);
    BULK_ORDER.updateUploadNotice();
    
    // Update the URL for storefront visits, but avoid rewriting Shopify editor preview URLs.
    if (!inDesignMode && typeof window.URL === 'function') {
      var nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set('step', stepNum);

      // Keep the configured canonical page path on storefront if we can safely identify it.
      if (pageUrl && window.location.pathname === pageUrl) {
        nextUrl.pathname = pageUrl;
      }

      if (nextUrl.toString() !== window.location.href) {
        history.pushState({ step: stepNum }, '', nextUrl.toString());
      }
    }
    
    // Scroll to top of the bulk order section
    if (bulkOrder && !inDesignMode) {
      bulkOrder.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  function isDesignMode() {
    var settings = window.BULK_ORDER_SETTINGS || {};
    if (typeof settings.designMode === 'boolean') {
      return settings.designMode;
    }

    return !!(window.Shopify && window.Shopify.designMode);
  }

  function loadStep2Content() {
    var step2Content = document.querySelector('.bulk-order-step-2');
    
    // If step 2 content already exists (rendered by Liquid), just show it
    if (step2Content) {
      step2Content.style.display = 'block';
      
      // Move blocks from container to target
      var blocksContainer = document.getElementById('BulkOrderBlocksContainer');
      var blocksTarget = document.getElementById('BulkOrderBlocksTarget');
      if (blocksContainer && blocksTarget && blocksContainer.children.length > 0) {
        while (blocksContainer.firstChild) {
          blocksTarget.appendChild(blocksContainer.firstChild);
        }
      }
      
      // Hide/show empty state based on whether blocks exist
      var emptyState = document.getElementById('BulkOrderCustomizationEmpty');
      if (emptyState && blocksTarget) {
        var hasBlocks = blocksTarget.children.length > 0;
        emptyState.style.display = hasBlocks ? 'none' : 'block';
      }
      
      // Initialize step 2 features
      initPerProductNotes();
      
      // Initialize conditional field logic for moved blocks
      initConditionalFields(blocksTarget);

      // Re-apply hide-when-design after move + conditionals (pages without SelectedDesignBox
      // never ran this on init; conditional eval can also run after first visibility pass).
      if (typeof window.updateFileUploadVisibility === 'function') {
        window.updateFileUploadVisibility();
      }
      
      // Update the order summary in step 2
      renderStep2OrderSummary();
      return;
    }

    // If step 2 content doesn't exist, we need to create it dynamically
    // This happens if the page was loaded at step 1 and we're navigating to step 2
    var contentArea = document.querySelector('.bulk-order-content');
    if (!contentArea) return;

    var settings = window.BULK_ORDER_SETTINGS || {};
    var step2Html = renderStep2Html(settings);
    
    // Create and insert step 2 content
    var step2Div = document.createElement('div');
    step2Div.className = 'bulk-order-step-2';
    step2Div.style.display = 'block';
    step2Div.innerHTML = step2Html;
    contentArea.appendChild(step2Div);
    
    // Move blocks from container
    var blocksContainer = document.getElementById('BulkOrderBlocksContainer');
    var blocksTarget = step2Div.querySelector('#BulkOrderBlocksTarget') || step2Div.querySelector('.bulk-order-customization__blocks');
    if (blocksContainer && blocksTarget) {
      while (blocksContainer.firstChild) {
        blocksTarget.appendChild(blocksContainer.firstChild);
      }
    }
    
    // Hide/show empty state based on whether blocks exist
    var emptyState = step2Div.querySelector('#BulkOrderCustomizationEmpty');
    if (emptyState && blocksTarget) {
      var hasBlocks = blocksTarget.children.length > 0;
      emptyState.style.display = hasBlocks ? 'none' : 'block';
    }
    
    initPerProductNotes();
    
    // Initialize conditional field logic for moved blocks
    initConditionalFields(blocksTarget);

    if (typeof window.updateFileUploadVisibility === 'function') {
      window.updateFileUploadVisibility();
    }
    
    renderStep2OrderSummary();
  }

  function renderStep2Html(settings) {
    var title = settings.step2_title || 'Customize Your Order';
    var description = settings.step2_description || 'Add your customization details below.';
    var enablePerProductNotes = settings.enable_per_product_notes !== false;
    var perProductNotesLabel = settings.per_product_notes_label || 'Notes for this item';

    var html = '<div class="bulk-order-step-2__header">';
    html += '<h2 class="bulk-order-step-title">' + escapeHtml(title) + '</h2>';
    html += '<p class="bulk-order-step-description">' + escapeHtml(description) + '</p>';
    html += '</div>';

    // Global customizations section
    html += '<div class="bulk-order-customization">';
    html += '<h3 class="bulk-order-customization__title">Global Customizations</h3>';
    html += '<p class="bulk-order-customization__subtitle">These settings apply to all items in your order</p>';
    html += '<div class="bulk-order-customization__blocks" id="BulkOrderBlocksTarget"></div>';
    html += '</div>';

    // Order summary with per-product notes
    html += '<div class="bulk-order-order-summary">';
    html += '<h3 class="bulk-order-order-summary__title">Your Items</h3>';
    html += '<div id="BulkOrderStep2Items" class="bulk-order-order-summary__items"></div>';
    html += '</div>';

    return html;
  }

  function renderStep2OrderSummary() {
    // Use the correct ID from bulk-order-step-2.liquid
    var container = document.getElementById('BulkOrderSummaryItems');
    if (!container) return;
    
    // Also update the empty state visibility
    var emptyState = document.querySelector('.bulk-order-order-summary__empty');
    
    // Expose this function globally for the snippet to call
    window.BULK_ORDER_RENDER_STEP2_SUMMARY = renderStep2OrderSummary;
    
    if (BULK_ORDER.orderList.length === 0) {
      container.innerHTML = '';
      if (emptyState) emptyState.style.display = 'block';
      return;
    }
    
    if (emptyState) emptyState.style.display = 'none';

    var html = '';
    BULK_ORDER.orderList.forEach(function(item) {
      html += '<div class="bulk-order-order-summary__item" data-product-id="' + item.productId + '" data-variant-id="' + item.variantId + '">';
      html += '<div class="bulk-order-order-summary__item-header">';
      if (item.image) {
        html += '<img src="' + item.image + '" alt="" class="bulk-order-order-summary__item-image" width="60" height="60">';
      }
      html += '<div class="bulk-order-order-summary__item-info">';
      html += '<strong class="bulk-order-order-summary__item-title">' + escapeHtml(item.title) + '</strong>';
      if (item.variantTitle) {
        html += '<span class="bulk-order-order-summary__item-variant">' + escapeHtml(item.variantTitle) + '</span>';
      }
      html += '<span class="bulk-order-order-summary__item-qty">Qty: ' + item.quantity + '</span>';
      html += '<span class="bulk-order-order-summary__item-price">' + BULK_ORDER.formatMoney(BULK_ORDER.getItemLinePrice(item)) + '</span>';
      html += '</div>';
      html += '</div>';
      html += '</div>';
    });

    container.innerHTML = html;
  }

  function loadStep3Content() {
    var step3Content = document.querySelector('.bulk-order-step-3');
    
    if (step3Content) {
      step3Content.style.display = 'block';
      initReviewStep();
      initCheckoutButton();
      return;
    }

    // Create step 3 dynamically if it doesn't exist
    var contentArea = document.querySelector('.bulk-order-content');
    if (!contentArea) return;

    var settings = window.BULK_ORDER_SETTINGS || {};
    var step3Html = renderStep3Html(settings);
    
    var step3Div = document.createElement('div');
    step3Div.className = 'bulk-order-step-3';
    step3Div.style.display = 'block';
    step3Div.innerHTML = step3Html;
    contentArea.appendChild(step3Div);
    
    initReviewStep();
    initCheckoutButton();
  }

  function renderStep3Html(settings) {
    var title = settings.step3_title || 'Review & Submit';
    var description = settings.step3_description || 'Review your order and proceed to checkout.';
    var checkoutText = settings.checkout_button_text || 'Proceed to Checkout';

    var html = '<div class="bulk-order-step-3__header">';
    html += '<h2 class="bulk-order-step-title">' + escapeHtml(title) + '</h2>';
    html += '<p class="bulk-order-step-description">' + escapeHtml(description) + '</p>';
    html += '</div>';

    html += '<div class="bulk-order-review">';
    html += '<div class="bulk-order-review__items" id="BulkOrderReviewItems"></div>';
    html += '<div class="bulk-order-review__customizations" id="BulkOrderReviewCustomizations"></div>';
    html += '<div class="bulk-order-review__total">';
    html += '<span class="bulk-order-review__total-label">Total:</span>';
    html += '<span class="bulk-order-review__total-value" id="BulkOrderReviewTotal">$0.00</span>';
    html += '</div>';
    html += '</div>';

    html += '<div class="bulk-order-actions">';
    html += '<button type="button" class="bulk-order-checkout-btn button button--primary" id="BulkOrderCheckout">' + escapeHtml(checkoutText) + '</button>';
    html += '</div>';

    return html;
  }

  function updateNavigationButtons(stepNum) {
    var navigation = document.querySelector('.bulk-order-navigation');
    if (!navigation) return;

    var settings = window.BULK_ORDER_SETTINGS || {};
    var prevText = settings.prev_button_text || 'Previous';
    var nextText = settings.next_button_text || 'Next';
    var pageUrl = settings.pageUrl || '/pages/bulk-order';

    var prevBtn = navigation.querySelector('.bulk-order-nav-btn--prev');
    var nextBtn = navigation.querySelector('.bulk-order-nav-btn--next');

    // Update prev button
    if (stepNum === '1') {
      if (prevBtn) prevBtn.style.display = 'none';
    } else {
      if (prevBtn) {
        prevBtn.style.display = '';
        prevBtn.href = pageUrl + '?step=' + (parseInt(stepNum) - 1);
      } else {
        // Create prev button if it doesn't exist
        var newPrevBtn = document.createElement('a');
        newPrevBtn.className = 'bulk-order-nav-btn bulk-order-nav-btn--prev';
        newPrevBtn.href = pageUrl + '?step=' + (parseInt(stepNum) - 1);
        newPrevBtn.innerHTML = '<span aria-hidden="true">←</span> ' + escapeHtml(prevText);
        navigation.insertBefore(newPrevBtn, navigation.firstChild);
      }
    }

    // Update next button
    if (stepNum === '3') {
      if (nextBtn) nextBtn.style.display = 'none';
    } else {
      if (nextBtn) {
        nextBtn.style.display = '';
        // Update disabled state
        if (stepNum === '1') {
          nextBtn.disabled = BULK_ORDER.orderList.length === 0;
        } else {
          nextBtn.disabled = false;
        }
      }
    }
  }

  // Validate required fields in customization blocks
  function validateStep2() {
    var blocksTarget = document.getElementById('BulkOrderBlocksTarget');
    if (!blocksTarget) return { valid: true, errors: [] };
    
    var errors = BULK_ORDER.validateUploads().errors;
    var firstInvalidField = null;
    
    // Find all required inputs in the customization blocks
    var requiredFields = blocksTarget.querySelectorAll('[required], [aria-required="true"]');
    
    requiredFields.forEach(function(field) {
      // Skip hidden/disabled fields
      if (field.type === 'file' || field.disabled || field.offsetParent === null) return;
      
      var isValid = true;
      var fieldLabel = '';
      
      // Get field label
      var label = field.closest('.product-file-upload, .product-custom-property, .product-custom-select, .product-custom-multiselect, .product-swatch-group');
      if (label) {
        var labelEl = label.querySelector('label, .form__label, legend');
        if (labelEl) {
          fieldLabel = labelEl.textContent.replace(/\*|required/gi, '').trim();
        }
      }
      if (!fieldLabel) {
        fieldLabel = field.name || field.id || 'Required field';
      }
      
      // Validate based on field type
      if (field.type === 'file') {
        isValid = field.files && field.files.length > 0;
      } else if (field.type === 'checkbox' || field.type === 'radio') {
        // For radio/checkbox groups, check if at least one is checked
        var name = field.name;
        if (name) {
          var group = blocksTarget.querySelectorAll('[name="' + name + '"]');
          isValid = Array.from(group).some(function(el) { return el.checked; });
        } else {
          isValid = field.checked;
        }
      } else {
        isValid = field.value && field.value.trim() !== '';
      }
      
      if (!isValid) {
        errors.push(fieldLabel);
        field.setAttribute('aria-invalid', 'true');
        
        // Show error message if available
        var errorEl = field.closest('.product-file-upload, .product-custom-property, .product-custom-select, .product-custom-multiselect, .product-swatch-group')?.querySelector('.error-message, [ref="errorMessage"]');
        if (errorEl) {
          errorEl.hidden = false;
        }
        
        if (!firstInvalidField) {
          firstInvalidField = field;
        }
      } else {
        field.removeAttribute('aria-invalid');
        var errorEl = field.closest('.product-file-upload, .product-custom-property, .product-custom-select, .product-custom-multiselect, .product-swatch-group')?.querySelector('.error-message, [ref="errorMessage"]');
        if (errorEl) {
          errorEl.hidden = true;
        }
      }
    });
    
    // Focus first invalid field
    if (firstInvalidField) {
      firstInvalidField.focus();
      firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    
    return { valid: errors.length === 0, errors: errors };
  }

  function initNavigation() {
    var settings = window.BULK_ORDER_SETTINGS || {};
    var pageUrl = settings.pageUrl || '/pages/bulk-order';

    // Handle Next button clicks
    var nextBtn = document.querySelector('.bulk-order-nav-btn--next');
    if (nextBtn) {
      nextBtn.addEventListener('click', function(e) {
        e.preventDefault();
        var currentStep = getCurrentStep();
        
        if (currentStep === '1') {
          // Only proceed if there are items in the order
          if (BULK_ORDER.orderList.length > 0) {
            var step1StockValidation = validateOrderInventory();
            if (!step1StockValidation.valid) {
              showBulkOrderValidationMessage('Not Enough Stock', step1StockValidation.errors.map(function(error) {
                return error.message;
              }));
              return;
            }
            showStep('2');
          }
        } else if (currentStep === '2') {
          var step2StockValidation = validateOrderInventory();
          if (!step2StockValidation.valid) {
            showBulkOrderValidationMessage('Not Enough Stock', step2StockValidation.errors.map(function(error) {
              return error.message;
            }));
            return;
          }

          // Validate required customization fields before proceeding to step 3
          var validation = validateStep2();
          if (!validation.valid) {
            // Show error message
            var errorMessage = 'Please complete the following required fields:\n• ' + validation.errors.join('\n• ');
            if (typeof Swal !== 'undefined') {
              Swal.fire({
                icon: 'warning',
                title: 'Required Fields Missing',
                html: '<p>Please complete the following required fields:</p><ul style="text-align:left;margin-top:10px;">' + 
                      validation.errors.map(function(e) { return '<li>' + escapeHtml(e) + '</li>'; }).join('') + '</ul>',
                confirmButtonColor: '#000'
              });
            } else {
              alert(errorMessage);
            }
            return;
          }
          showStep('3');
        }
      });
    }

    // Handle Prev button clicks (it's an <a> element, so we can let it use href)
    var prevBtn = document.querySelector('.bulk-order-nav-btn--prev');
    if (prevBtn) {
      prevBtn.addEventListener('click', function(e) {
        e.preventDefault();
        var currentStep = getCurrentStep();
        
        if (currentStep === '2') {
          showStep('1');
        } else if (currentStep === '3') {
          showStep('2');
        }
      });
    }
  }

  function escapeHtml(text) {
    if (!text) return '';
    var div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // ==========================================================================
  // Mobile Image Popup
  // ==========================================================================

  function initMobileImagePopup() {
    var modal = document.getElementById('BulkOrderImageModal');
    if (!modal) return;
    
    var overlay = modal.querySelector('.bulk-order-image-modal__overlay');
    var closeBtn = modal.querySelector('.bulk-order-image-modal__close');
    var modalImage = document.getElementById('BulkOrderModalImage');
    var modalCaption = document.getElementById('BulkOrderModalCaption');
    
    // Check if mobile popup is enabled (settings passed to DOM)
    var settings = window.BULK_ORDER_SETTINGS || {};
    var isMobilePopupEnabled = settings.showMobileImagePopup !== false;
    
    if (!isMobilePopupEnabled) return;
    
    // Detect if device is touch-capable
    function isTouchDevice() {
      return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    }
    
    // Only enable on mobile/touch devices
    if (!isTouchDevice()) return;
    
    // Show modal with image
    function showModal(imageSrc, caption) {
      if (!modalImage) return;
      
      modalImage.src = imageSrc;
      modalImage.alt = caption || '';
      if (modalCaption) {
        modalCaption.textContent = caption || '';
      }
      
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      
      // Focus the close button for accessibility
      if (closeBtn) {
        setTimeout(function() {
          closeBtn.focus();
        }, 100);
      }
    }
    
    // Hide modal
    function hideModal() {
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      
      // Clear image to prevent flash of old content
      setTimeout(function() {
        if (modalImage) {
          modalImage.src = '';
          modalImage.alt = '';
        }
        if (modalCaption) {
          modalCaption.textContent = '';
        }
      }, 200);
    }
    
    // Close on overlay click
    if (overlay) {
      overlay.addEventListener('click', hideModal);
    }
    
    // Close on button click
    if (closeBtn) {
      closeBtn.addEventListener('click', hideModal);
    }
    
    // Close on Escape key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && modal.getAttribute('aria-hidden') === 'false') {
        hideModal();
      }
    });
    
    // Add tap handler to dropdown options with images
    document.addEventListener('click', function(e) {
      // Check if we're on mobile (viewport width)
      if (window.innerWidth > 768) return;
      
      var option = e.target.closest('.bulk-order-dropdown__option');
      if (!option) return;
      
      var swatch = option.querySelector('.bulk-order-dropdown__option-swatch');
      if (!swatch) return;
      
      // Check if the swatch was tapped directly
      var swatchRect = swatch.getBoundingClientRect();
      var clickX = e.clientX;
      var clickY = e.clientY;
      
      // If click was within swatch bounds, show modal
      if (clickX >= swatchRect.left && clickX <= swatchRect.right &&
          clickY >= swatchRect.top && clickY <= swatchRect.bottom) {
        e.preventDefault();
        e.stopPropagation();
        
        // Get larger image from tooltip or swatch
        var tooltip = option.querySelector('.bulk-order-dropdown__tooltip img');
        var preview = option.querySelector('.bulk-order-dropdown__option-preview img');
        var imageSrc = '';
        
        if (preview) {
          imageSrc = preview.src;
        } else if (tooltip) {
          imageSrc = tooltip.src;
        } else {
          // Extract from background-image
          var bgStyle = swatch.style.backgroundImage;
          var match = bgStyle.match(/url\(['"]?([^'"]+)['"]?\)/);
          if (match) {
            // Replace width parameter for larger image
            imageSrc = match[1].replace(/width=\d+/, 'width=600');
          }
        }
        
        if (imageSrc) {
          var text = option.querySelector('.bulk-order-dropdown__option-text');
          var caption = text ? text.textContent.trim() : '';
          showModal(imageSrc, caption);
        }
      }
    }, true);
    
    // Expose for potential external use
    window.BULK_ORDER_IMAGE_MODAL = {
      show: showModal,
      hide: hideModal
    };
  }

  // ==========================================================================
  // Collection Filtering
  // ==========================================================================
  
  function initCollectionFilters() {
    // Collection filters are handled inline in bulk-order-step-1.liquid
    // This function is a fallback/placeholder for any additional filtering logic
    // The actual filtering is done by the inline script in the snippet
    // to ensure it works even without the full bulk-order.js loading
  }

  // ==========================================================================
  // Pricing Note
  // ==========================================================================

  function initPricingNote() {
    var settings = window.BULK_ORDER_SETTINGS || {};
    if (!settings.enablePricingNote || !settings.pricingNoteText) return;
    var notePosition = settings.pricingNotePosition || 'centered';
    var placeUnderSubtotal = notePosition === 'under_subtotal';
    
    // Set the note text on all pricing note elements
    var noteElements = document.querySelectorAll('[data-pricing-note]');
    noteElements.forEach(function(el) {
      el.textContent = settings.pricingNoteText;
      el.classList.toggle('bulk-order-pricing-note--under-subtotal', placeUnderSubtotal);
    });

    document.querySelectorAll('.bulk-order-review__pricing').forEach(function(el) {
      el.classList.toggle('bulk-order-review__pricing--note-under-subtotal', placeUnderSubtotal);
    });
  }

  // ==========================================================================
  // Conditional Field Logic for Bulk Order
  // ==========================================================================

  /**
   * Initialize conditional display fields for bulk order blocks.
   * This handles the case where blocks don't have a parent form (unlike product pages).
   * The block's inline scripts may have already run but failed because there's no form.
   * We reset and re-initialize to make conditional logic work in the bulk order context.
   * @param {Element} container - The container element with the blocks
   */
  function initConditionalFields(container) {
    if (!container) return;
    
    var conditionalFields = container.querySelectorAll('[data-cond-key]');

    function syncConditionalFieldHost(field, isVisible) {
      if (!(field instanceof Element)) return;

      var layoutItem = field;
      while (layoutItem.parentElement && layoutItem.parentElement !== container) {
        layoutItem = layoutItem.parentElement;
      }

      if (!(layoutItem instanceof HTMLElement) || layoutItem === container || layoutItem.parentElement !== container) {
        return;
      }

      // Hide the actual flex item inside the customization blocks stack so any
      // editor/runtime wrapper disappears along with the conditional block.
      layoutItem.hidden = !isVisible;
      layoutItem.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
    }
    
    conditionalFields.forEach(function(field) {
      // Skip if already initialized by bulk order
      if (field.dataset.bulkCondInit) return;
      
      var key = field.getAttribute('data-cond-key');
      if (!key) return;
      
      // Reset any previous initialization state from the block's inline scripts
      // since they likely failed (no form found in bulk order context)
      delete field.dataset.condInit;
      
      // In bulk order, we look for controlling fields within the same container
      // since there's no form. This allows fields in the same customization area
      // to control each other's visibility.
      var searchScope = container;
      
      var evaluateCondition = function() {
        var op = field.getAttribute('data-cond-op') || 'has_any_value';
        var values = (field.getAttribute('data-cond-values') || '')
          .split(',')
          .map(function(s) { return s.trim(); })
          .filter(Boolean)
          .map(function(s) { return s.toLowerCase(); });
        
        var valueResult = readFormValue(searchScope, key);
        var show = false;
        
        if (valueResult.found) {
          var v = (valueResult.value || '').trim().toLowerCase();
          if (op === 'has_any_value') {
            show = v !== '';
          } else if (op === 'is_any_of') {
            show = values.length ? values.indexOf(v) > -1 : v !== '';
          } else if (op === 'is_not_any_of') {
            show = values.length ? values.indexOf(v) === -1 : true;
          }
        }
        
        // Handle visibility transitions
        var isInitialLoad = !field.dataset.bulkCondInit;
        var wasVisible = field.classList.contains('is-visible') || field.getAttribute('aria-hidden') === 'false';
        if (show) {
          syncConditionalFieldHost(field, true);
          field.hidden = false;
          field.setAttribute('aria-hidden', 'false');
          if (!isInitialLoad) {
            requestAnimationFrame(function() {
              field.classList.add('is-visible');
            });
          } else {
            field.classList.add('is-visible');
          }
        } else {
          if (!isInitialLoad && wasVisible) {
            resetConditionalPropertyValues(field);
          }
          field.classList.remove('is-visible');
          field.setAttribute('aria-hidden', 'true');
          if (!isInitialLoad) {
            setTimeout(function() {
              if (field.getAttribute('aria-hidden') === 'true') {
                field.hidden = true;
                syncConditionalFieldHost(field, false);
              }
            }, 300);
          } else {
            field.hidden = true;
            syncConditionalFieldHost(field, false);
          }
        }
        setFieldsDisabled(field, !show);
      };
      
      // Find and attach listeners to controlling fields within the container
      var escaped = window.CSS && CSS.escape ? CSS.escape(key) : key;
      var selector = '[name="properties[' + escaped + ']"]';
      var controllingElements = searchScope.querySelectorAll(selector);
      
      controllingElements.forEach(function(el) {
        el.addEventListener('change', evaluateCondition);
        el.addEventListener('input', evaluateCondition);
      });
      
      // Also search in document in case controlling field is outside container
      // (e.g., rendered by a different section)
      if (controllingElements.length === 0) {
        var docElements = document.querySelectorAll(selector);
        docElements.forEach(function(el) {
          el.addEventListener('change', evaluateCondition);
          el.addEventListener('input', evaluateCondition);
        });
      }
      
      // Initial evaluation
      evaluateCondition();
      field.dataset.bulkCondInit = 'true';
    });
  }
  
  /**
   * Read form value for a property key
   * @param {Element} scope - The scope to search in (form, container, or document)
   * @param {string} key - The property key
   * @returns {{found: boolean, value: string}}
   */
  function readFormValue(scope, key) {
    if (!scope) return { found: false, value: '' };
    
    var escaped = window.CSS && CSS.escape ? CSS.escape(key) : key;
    var selector = '[name="properties[' + escaped + ']"]';
    var elements = Array.prototype.filter.call(scope.elements || scope.querySelectorAll(selector), function(element) {
      return element.matches(selector) && !element.disabled;
    });
    
    if (!elements || elements.length === 0) {
      return { found: false, value: '' };
    }
    
    if (elements.some(function(element) { return element.type === 'radio'; })) {
      var checked = elements.find(function(element) { return element.type === 'radio' && element.checked; });
      return { 
        found: true, 
        value: (checked && checked.value) || '' 
      };
    }
    
    if (elements.some(function(element) { return element.type === 'checkbox'; })) {
      var checkedBox = elements.find(function(element) { return element.type === 'checkbox' && element.checked; });
      return { found: true, value: checkedBox ? (checkedBox.value || '') : '' };
    }
    
    var filled = elements.find(function(element) {
      return (element.value || '').trim() !== '';
    });
    return { found: true, value: (filled || elements[0]).value || '' };
  }

  /**
   * Dispatch value-change events so dependent conditionals and fee logic update.
   * @param {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} el - The changed form control
   */
  function emitValueChange(el) {
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Keep enhanced custom-select text in sync after programmatic reset.
   * @param {HTMLSelectElement} select - The native select backing the enhanced control
   */
  function resetEnhancedSelectLabel(select) {
    var enhanced = select.closest('.js-custom-select');
    if (!enhanced) return;

    var valueEl = enhanced.querySelector('.custom-select__value');
    var emptyOption = select.querySelector('option[value=""]');
    var placeholderOption = enhanced.querySelector('.custom-select__option--placeholder');
    var placeholder = (emptyOption && emptyOption.textContent) || (placeholderOption && placeholderOption.textContent) || '';
    if (valueEl) valueEl.textContent = placeholder;
  }

  /**
   * Clear property-bearing conditional field values before hiding them.
   * @param {HTMLElement} container - The conditional field container
   */
  function resetConditionalPropertyValues(container) {
    if (!container.matches('.custom-select, .custom-multi, .custom-property, .product-file-upload, .swatch-group, .design-selector')) {
      return;
    }

    container.querySelectorAll('input, select, textarea').forEach(function(el) {
      if (el instanceof HTMLSelectElement) {
        var before = el.value;
        var hasEmptyOption = Array.prototype.some.call(el.options, function(option) {
          return option.value === '';
        });
        if (hasEmptyOption) {
          el.value = '';
        } else {
          el.selectedIndex = -1;
        }
        resetEnhancedSelectLabel(el);
        if (el.value !== before) emitValueChange(el);
      } else if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) {
        if (el.checked) {
          el.checked = false;
          emitValueChange(el);
        }
      } else if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
        if (el.value !== '') {
          el.value = '';
          emitValueChange(el);
        }
      }
    });
  }
  
  /**
   * Set disabled state on all form fields within container
   * @param {Element} container - The container element
   * @param {boolean} disabled - Whether to disable the fields
   */
  function setFieldsDisabled(container, disabled) {
    if (container.dataset.designHidden === 'true') {
      container.dataset.designConditionalDisabled = String(disabled);
      disabled = true;
    }
    container.querySelectorAll('input, select, textarea, button').forEach(function(el) {
      if (disabled) {
        if (el.required) {
          el.setAttribute('data-was-required', 'true');
          el.required = false;
        }
        el.disabled = true;
      } else {
        el.disabled = false;
        if (el.getAttribute('data-was-required') === 'true') {
          el.required = true;
          el.removeAttribute('data-was-required');
        }
      }
    });
  }

  // ==========================================================================
  // Initialization
  // ==========================================================================

  function init() {
    BULK_ORDER.init();
    
    // Detect current step from URL and show appropriate content
    var currentStep = getCurrentStep();
    showStep(currentStep);
    BULK_ORDER.updateUploadNotice();
    if (window.HATLAB_DESIGN) HATLAB_DESIGN.updateSelectedDesignBox();

    // Initialize common functionality
    initDropdowns();
    initVariantImageTooltips();
    initQuantityControls();
    initAddVariantButtons();
    renderAllAddedVariants();
    initAddButtons();
    initSidebarRemove();
    initMobileSidebar();
    initNavigation();
    initDescriptionToggles();
    initMobileImagePopup();
    initCollectionFilters();
    initPricingNote();
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Handle browser back/forward navigation
  if (!isDesignMode()) {
    window.addEventListener('popstate', function(e) {
      var currentStep = getCurrentStep();
      showStep(currentStep);
    });
  }

  // Re-init on Shopify section load (theme editor)
  document.addEventListener('shopify:section:load', function(e) {
    if (e.target.querySelector('.bulk-order')) {
      init();
    }
  });

})();
